import type {
  AvatarScript,
  CaptureOutput,
  CapturePublishReceipt,
  CapturePublishReceiptInput,
  CaptureOutputInput,
  CaptureOutputProvenance,
  CapturePlatformMode,
  CaptureRefinementMode,
  CaptureOutputStatus,
  DashboardPayload,
  DeepDiveScript,
  GovernanceResearchBrief,
  PithySignalAssessmentInput,
  PithySignalAssessmentReport,
  PithyPaletteId,
  PithyNote,
  PrivateSourceRecord,
  PrivateSourceSearchResult,
  ReportWorkflow,
  ResearchLibraryRecord,
  ResearchBriefMode,
  SaveResearchLibraryRecordInput,
  ScholarAiPaperResult,
  WeeklyBrief,
  WeeklyBriefAiSummary,
} from "../domain/types.ts";
import { buildWeeklyBrief } from "../domain/briefing.ts";
import { formatSourceUrlCitation } from "../domain/references.ts";

type SignalView =
  | "signal"
  | "capture"
  | "create"
  | "outputs"
  | "brief"
  | "enrichment"
  | "library"
  | "profile"
  | "tags"
  | "sources"
  | "archive"
  | "trash";

type AiProvider = "openai" | "claude" | "gemini";

interface SessionState {
  authEnabled: boolean;
  authenticated: boolean;
  workspaceMode: "cloud" | "local";
  aiUsageMode: "provider" | "local";
  aiCredentialMode: "workspace" | "user" | "local";
  aiProviderLabel: string | null;
  byokEnabled: boolean;
  exportEnabled: boolean;
  deleteEnabled: boolean;
  workspaceId?: string | null;
  workspaceUser?: { id: string; email: string; name: string } | null;
  includedAiUsage?: {
    limit: number;
    used: number;
    remaining: number;
    period: string;
  };
}

interface CaptureComposerState {
  title: string;
  sourceUrl: string;
  sourceKind: PithyNote["source"]["kind"];
  capturedText: string;
  category: string;
  summary: string;
  signalStrength: PithyNote["signalStrength"];
  riskType: PithyNote["riskType"];
  suggestedAction: PithyNote["suggestedAction"];
  tags: string;
  slide: PithyNote["slide"];
}

interface BriefFilterState {
  category: string;
  tag: string;
  fromDate: string;
  toDate: string;
}

interface OutputsFilterState {
  status: "all" | CaptureOutputStatus;
  platform: "all" | CapturePlatformMode;
  palette: "all" | PithyPaletteId;
  sort: "recent" | "oldest" | "published-first" | "source-title";
}

interface SourceRecord {
  id: string;
  title: string;
  sourceLabel: string;
  sourceKind: string;
  url?: string;
  capturedAt: string;
  citation: string;
}

interface UploadedResearchDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  text: string;
  addedAt: string;
}

interface ResearchComposerState {
  workflow: ReportWorkflow;
  mode: ResearchBriefMode;
  title: string;
  sourceUrl: string;
  sourceUrls: string[];
  paperText: string;
  documents: UploadedResearchDocument[];
  privateSourceQuery: string;
  scholarQuery: string;
}

interface AssessmentComposerState extends PithySignalAssessmentInput {}

interface CreateComposerState {
  sourceMode: "existing-note" | "paste";
  sourceNoteId: string;
  rawThought: string;
  platform: CapturePlatformMode;
  palette: PithyPaletteId;
}

interface CreatePublishReceiptState {
  reviewerName: string;
  approvalNote: string;
}

interface AppState {
  notes: PithyNote[];
  brief: WeeklyBrief | null;
  selectedNoteId: string | null;
  search: string;
  activeView: SignalView;
  status: string;
  isLoading: boolean;
  session: SessionState;
  authPassword: string;
  authError: string;
  isSubmittingAuth: boolean;
  captureComposer: CaptureComposerState;
  isDraftingCapture: boolean;
  isSavingCapture: boolean;
  captureMessage: string;
  isBriefOpen: boolean;
  briefAiSummary: WeeklyBriefAiSummary | null;
  isLoadingBriefAiSummary: boolean;
  briefAiSummaryScopeKey: string;
  briefFilters: BriefFilterState;
  researchComposer: ResearchComposerState;
  assessmentComposer: AssessmentComposerState;
  assessmentReport: PithySignalAssessmentReport | null;
  researchBrief: GovernanceResearchBrief | null;
  researchScript: AvatarScript | null;
  researchDeepDive: DeepDiveScript | null;
  activeLibraryRecordId: string | null;
  isGeneratingResearchBrief: boolean;
  isGeneratingResearchScript: boolean;
  isGeneratingResearchDeepDive: boolean;
  isGeneratingBriefingVideoRecordId: string | null;
  researchMessage: string;
  libraryRecords: ResearchLibraryRecord[];
  isLoadingLibrary: boolean;
  librarySearch: string;
  privateSources: PrivateSourceRecord[];
  privateSourceResults: PrivateSourceSearchResult[];
  isLoadingPrivateSources: boolean;
  isSearchingPrivateSources: boolean;
  privateSourceMessage: string;
  scholarAiResults: ScholarAiPaperResult[];
  isSearchingScholarAi: boolean;
  scholarAiMessage: string;
  createComposer: CreateComposerState;
  createPublishReceipt: CreatePublishReceiptState;
  createMessage: string;
  createOutput: CaptureOutput | null;
  isGeneratingCreate: boolean;
  createHistory: CaptureOutput[];
  isLoadingCreateHistory: boolean;
  outputsLibrary: CaptureOutput[];
  isLoadingOutputsLibrary: boolean;
  outputsFilters: OutputsFilterState;
  isProfileMenuOpen: boolean;
  userApiKey: string;
  userAiProvider: AiProvider;
  embeddingApiKey: string;
  scholarAiApiKey: string;
  regulationsApiKey: string;
  nvdApiKey: string;
}

const navigationItems: Array<{
  id: SignalView;
  label: string;
  icon: string;
  badge?: () => number | null;
}> = [
  { id: "enrichment", label: "Research", icon: "spark" },
  { id: "create", label: "Signal Cards", icon: "doc" },
  { id: "outputs", label: "Outputs", icon: "archive" },
  { id: "library", label: "Library", icon: "archive" },
  { id: "sources", label: "Sources", icon: "doc" },
  { id: "profile", label: "Profile", icon: "user" },
];

const ecosystemLinks = [
  { href: "https://pithynotes.com/", label: "Home" },
  { href: "https://pithynotes.com/#our-story", label: "Our Story" },
  { href: "https://pithynotes.com/#system", label: "Products" },
];

const USER_API_KEY_STORAGE_KEY = "pithy-signal-user-api-key";
const USER_AI_PROVIDER_STORAGE_KEY = "pithy-signal-ai-provider";
const EMBEDDING_API_KEY_STORAGE_KEY = "pithy-signal-embedding-api-key";
const SCHOLAR_AI_API_KEY_STORAGE_KEY = "pithy-signal-scholarai-api-key";
const REGULATIONS_API_KEY_STORAGE_KEY = "pithy-signal-regulations-api-key";
const NVD_API_KEY_STORAGE_KEY = "pithy-signal-nvd-api-key";
let state: AppState = {
  notes: [],
  brief: null,
  selectedNoteId: null,
  search: "",
  activeView: "enrichment",
  status: "Loading Pithy Signal workspace...",
  isLoading: true,
  session: {
    authEnabled: false,
    authenticated: true,
    workspaceMode: "cloud",
    aiUsageMode: "local",
    aiCredentialMode: "local",
    aiProviderLabel: null,
    byokEnabled: false,
    exportEnabled: true,
    deleteEnabled: true,
    workspaceId: null,
    workspaceUser: null,
  },
  authPassword: "",
  authError: "",
  isSubmittingAuth: false,
  captureComposer: createEmptyCaptureComposer(),
  isDraftingCapture: false,
  isSavingCapture: false,
  captureMessage: "Paste text, add a URL, and draft a signal directly inside the app.",
  isBriefOpen: false,
  briefAiSummary: null,
  isLoadingBriefAiSummary: false,
  briefAiSummaryScopeKey: "",
  briefFilters: {
    category: "",
    tag: "",
    fromDate: "",
    toDate: "",
  },
  researchComposer: createEmptyResearchComposer(),
  assessmentComposer: createEmptyAssessmentComposer(),
  assessmentReport: null,
  researchBrief: null,
  researchScript: null,
  researchDeepDive: null,
  activeLibraryRecordId: null,
  isGeneratingResearchBrief: false,
  isGeneratingResearchScript: false,
  isGeneratingResearchDeepDive: false,
  isGeneratingBriefingVideoRecordId: null,
  researchMessage: "",
  libraryRecords: [],
  isLoadingLibrary: false,
  librarySearch: "",
  privateSources: [],
  privateSourceResults: [],
  isLoadingPrivateSources: false,
  isSearchingPrivateSources: false,
  privateSourceMessage: "Save documents once, then search and attach relevant excerpts to new briefs.",
  scholarAiResults: [],
  isSearchingScholarAi: false,
  scholarAiMessage: "Use ScholarAI to find peer-reviewed papers, then attach the useful ones.",
  createComposer: createEmptyCreateComposer(),
  createPublishReceipt: createEmptyPublishReceipt(),
  createMessage: "Turn one rough idea into one piece of clear, usable value language.",
  createOutput: null,
  isGeneratingCreate: false,
  createHistory: [],
  isLoadingCreateHistory: false,
  outputsLibrary: [],
  isLoadingOutputsLibrary: false,
  outputsFilters: {
    status: "all",
    platform: "all",
    palette: "all",
    sort: "recent",
  },
  isProfileMenuOpen: false,
  userApiKey: getStoredUserApiKey(),
  userAiProvider: getStoredUserAiProvider(),
  embeddingApiKey: getStoredSessionSecret(EMBEDDING_API_KEY_STORAGE_KEY),
  scholarAiApiKey: getStoredSessionSecret(SCHOLAR_AI_API_KEY_STORAGE_KEY),
  regulationsApiKey: getStoredSessionSecret(REGULATIONS_API_KEY_STORAGE_KEY),
  nvdApiKey: getStoredSessionSecret(NVD_API_KEY_STORAGE_KEY),
};

let root: HTMLElement | null = null;

function getStoredUserApiKey(): string {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return window.sessionStorage.getItem(USER_API_KEY_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function persistUserApiKey(value: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (value.trim()) {
      window.sessionStorage.setItem(USER_API_KEY_STORAGE_KEY, value.trim());
      return;
    }

    window.sessionStorage.removeItem(USER_API_KEY_STORAGE_KEY);
  } catch {
    // Some in-app browsers block sessionStorage access; fail quietly.
  }
}

function getStoredSessionSecret(storageKey: string): string {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return window.sessionStorage.getItem(storageKey) ?? "";
  } catch {
    return "";
  }
}

function persistSessionSecret(storageKey: string, value: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (value.trim()) {
      window.sessionStorage.setItem(storageKey, value.trim());
      return;
    }

    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Some in-app browsers block sessionStorage access; fail quietly.
  }
}

function getStoredUserAiProvider(): AiProvider {
  if (typeof window === "undefined") {
    return "openai";
  }

  try {
    return normalizeUserAiProvider(
      window.sessionStorage.getItem(USER_AI_PROVIDER_STORAGE_KEY) ?? undefined,
    );
  } catch {
    return "openai";
  }
}

function persistUserAiProvider(value: AiProvider): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(USER_AI_PROVIDER_STORAGE_KEY, value);
  } catch {
    // Some in-app browsers block sessionStorage access; fail quietly.
  }
}

function normalizeUserAiProvider(value: string | undefined): AiProvider {
  return value === "claude" || value === "gemini" ? value : "openai";
}

function formatUserAiProvider(value: AiProvider): string {
  switch (value) {
    case "claude":
      return "Claude";
    case "gemini":
      return "Gemini";
    default:
      return "OpenAI";
  }
}

export function mountApp(element: Element | null): void {
  if (!(element instanceof HTMLElement)) {
    throw new Error("App mount root is missing.");
  }

  root = element;
  window.addEventListener("hashchange", syncViewFromHash);
  syncViewFromHash();
  render();
  void initializeApp();
}

async function initializeApp(): Promise<void> {
  const userApiKey = getStoredUserApiKey();
  const userAiProvider = getStoredUserAiProvider();
  const session = await requestJson<SessionState>("/api/session");
  state = {
    ...state,
    session,
    userApiKey,
    userAiProvider,
    isLoading: false,
    status:
      session.authEnabled && !session.authenticated
        ? "Enter your Pithy Signal password."
        : "Pithy Signal workspace ready.",
  };
  render();

  if (!session.authEnabled || session.authenticated) {
    await loadDashboard();
    await loadOutputsLibrary();
    await loadLibraryRecords();
    await loadPrivateSources();
  }
}

async function loadDashboard(): Promise<void> {
  state = { ...state, isLoading: true };
  render();
  try {
    const payload = await requestJson<DashboardPayload>("/api/dashboard");
    state = {
      ...state,
      notes: payload.notes,
      brief: payload.brief,
      selectedNoteId: state.selectedNoteId ?? payload.notes[0]?.id ?? null,
      status: "Pithy Signal workspace ready.",
      isLoading: false,
      briefAiSummary: null,
      briefAiSummaryScopeKey: "",
    };
    render();
  } catch (error) {
    if (error instanceof Error && error.message.includes("401")) {
      state = {
        ...state,
        notes: [],
        brief: null,
        selectedNoteId: null,
        isLoading: false,
        session: { ...state.session, authEnabled: true, authenticated: false },
        status: "Enter your Pithy Signal password.",
      };
      render();
      return;
    }
    throw error;
  }
}

async function refreshSessionStatus(): Promise<void> {
  const session = await requestJson<SessionState>("/api/session");
  state = {
    ...state,
    session,
  };
  render();
}

async function loadOutputsLibrary(): Promise<void> {
  state = { ...state, isLoadingOutputsLibrary: true };
  render();
  try {
    const response = await requestJson<{ outputs: CaptureOutput[] }>("/api/capture-outputs");
    state = {
      ...state,
      outputsLibrary: response.outputs,
      isLoadingOutputsLibrary: false,
    };
    render();
  } catch {
    state = {
      ...state,
      outputsLibrary: [],
      isLoadingOutputsLibrary: false,
    };
    render();
  }
}

async function loadLibraryRecords(): Promise<void> {
  state = { ...state, isLoadingLibrary: true };
  render();
  try {
    const response = await requestJson<{ records: ResearchLibraryRecord[] }>("/api/library-records");
    state = {
      ...state,
      libraryRecords: response.records,
      isLoadingLibrary: false,
    };
    render();
  } catch {
    state = {
      ...state,
      libraryRecords: [],
      isLoadingLibrary: false,
    };
    render();
  }
}

async function loadPrivateSources(): Promise<void> {
  state = { ...state, isLoadingPrivateSources: true };
  render();
  try {
    const response = await requestJson<{ sources: PrivateSourceRecord[] }>("/api/private-sources");
    state = {
      ...state,
      privateSources: response.sources,
      isLoadingPrivateSources: false,
    };
    render();
  } catch {
    state = {
      ...state,
      privateSources: [],
      isLoadingPrivateSources: false,
    };
    render();
  }
}

function render(): void {
  if (!root) {
    return;
  }
  root.innerHTML =
    state.session.authEnabled && !state.session.authenticated
      ? buildAuthMarkup()
      : `${buildAppMarkup()}${state.isBriefOpen ? buildBriefModalMarkup() : ""}`;
  bindEvents();
}

function syncViewFromHash(): void {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) {
    return;
  }
  const previousView = state.activeView;
  const allowedViews = new Set<SignalView>([
    "signal",
    "capture",
    "create",
    "outputs",
    "brief",
    "enrichment",
    "library",
    "profile",
    "tags",
    "sources",
    "archive",
    "trash",
  ]);
  if (!allowedViews.has(hash as SignalView)) {
    return;
  }
  const nextView =
    hash === "signal" || hash === "capture"
      ? "enrichment"
      : hash === "brief"
        || hash === "tags"
        || hash === "archive"
        || hash === "trash"
        ? "enrichment"
        : (hash as SignalView);
  state = {
    ...state,
    activeView: nextView,
    isBriefOpen: false,
    isProfileMenuOpen: false,
  };
  if (root && previousView !== state.activeView) {
    render();
  }
}

function navigateToView(nextView: SignalView): void {
  const resolvedView = nextView === "brief" ? "enrichment" : nextView;
  state = {
    ...state,
    activeView: resolvedView,
    isProfileMenuOpen: false,
    isBriefOpen: false,
  };
  if (window.location.hash.replace(/^#/, "") !== resolvedView) {
    window.location.hash = resolvedView;
  }
  render();
}

function openProfileView(): void {
  state = {
    ...state,
    activeView: "profile",
    isProfileMenuOpen: false,
    isBriefOpen: false,
  };
  if (window.location.hash.replace(/^#/, "") !== "profile") {
    window.location.hash = "profile";
  }
  render();
}

function buildAuthMarkup(): string {
  return `
    <div class="auth-shell">
      <section class="auth-card" aria-labelledby="auth-title">
        <img class="auth-logo-image" src="/pithy-notes-logo.png" alt="Pithy Notes" />
        <div class="auth-entry-copy">
          <h1 id="auth-title">Pithy Signal</h1>
          <p class="auth-helper">Find the value signal in your work.</p>
        </div>

        <form class="auth-form" id="auth-form">
          <label class="auth-field">
            <span>Password</span>
            <input id="auth-password" type="password" value="${escapeAttribute(state.authPassword)}" placeholder="Enter app password" autocomplete="current-password" />
          </label>
          ${
            state.authError
              ? `<p class="auth-error" role="alert">${escapeHtml(state.authError)}</p>`
              : `<p class="auth-helper">Use the password configured for your Pithy Signal deployment.</p>`
          }
          <button class="auth-submit" type="submit" ${state.isSubmittingAuth ? "disabled" : ""}>
            ${state.isSubmittingAuth ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </section>
    </div>
  `;
}

function buildAppMarkup(): string {
  const filteredNotes = getFilteredNotes();
  const selectedNote = getSelectedNote(filteredNotes) ?? state.notes[0] ?? null;
  const brief = state.brief;
  const newCount = filteredNotes.length;
  const detailThemes = selectedNote ? buildThemeLabels(selectedNote) : [];
  const contentMarkup =
    state.activeView === "library"
      ? buildLibraryViewMarkup()
      : state.activeView === "profile"
        ? buildProfileViewMarkup()
      : state.activeView === "sources"
        ? buildSourcesViewMarkup(filteredNotes)
      : state.activeView === "create"
        ? buildCreateWorkspaceMarkup(filteredNotes)
      : state.activeView === "outputs"
        ? buildOutputsLibraryMarkup()
        : state.activeView === "enrichment"
          ? buildResearchPipelineMarkup()
          : buildCaptureWorkspaceMarkup(filteredNotes, newCount);

  return `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand-lockup">
          <img class="brand-lockup-image" src="/pithy-notes-lockup.png" alt="Pithy Notes" />
        </div>

        <nav class="sidebar-ecosystem-nav" aria-label="Pithy Notes ecosystem">
          ${ecosystemLinks
            .map(
              (item) => `
                <a href="${escapeAttribute(item.href)}" target="_blank" rel="noreferrer">
                  <span>${escapeHtml(item.label)}</span>
                  <span class="ecosystem-link-icon">${buildExternalIcon()}</span>
                </a>
              `,
            )
            .join("")}
        </nav>

        <nav class="sidebar-nav">
          ${navigationItems.map((item) => buildNavItem(item)).join("")}
        </nav>

        <div class="sidebar-account-nav">
          <button class="nav-item nav-item-danger" data-action="signout">
            <span class="nav-icon">${buildSidebarIcon("logout")}</span>
            <span class="nav-label">Log off</span>
          </button>
        </div>

        <div class="quote-card">
          <span class="quote-signature">Signal layer</span>
          <p>Pithy Signal helps clarify your own expertise, reflections, and notes.</p>
        </div>

      </aside>

      <main class="product-frame">
        <header class="topbar">
          <div class="topbar-title">
            <div class="title-lockup">
              <p class="topbar-eyebrow">Workspace</p>
              <h1>${escapeHtml(getWorkspaceTitle(state.activeView))}</h1>
            </div>
          </div>

          <div class="topbar-tools">
            <label class="searchbar">
              <span class="searchbar-icon">${buildSearchIcon()}</span>
              <input id="search-input" value="${escapeAttribute(state.search)}" placeholder="Search signals, tags, or sources" />
            </label>
          </div>
        </header>

        ${contentMarkup}
      </main>
    </div>
  `;
}

function buildCaptureWorkspaceMarkup(filteredNotes: PithyNote[], newCount: number): string {
  return `
    <div class="sources-view inbox-view inbox-workspace">
      <section class="sources-library inbox-capture-shell">
        ${buildCaptureComposerMarkup()}
      </section>

      <section class="sources-library inbox-shell">
        <header class="section-header inbox-header">
          <div>
            <h2>Inbox</h2>
            <p>Your saved notes and reflections waiting to be reviewed and turned into clearer language.</p>
          </div>
          <div class="section-meta">
            <span class="new-count">${newCount} new</span>
          </div>
        </header>

        <div class="signal-list">
          ${
            filteredNotes.length
              ? filteredNotes
                  .map((note) => buildSignalCard(note, note.id === state.selectedNoteId))
                  .join("")
              : `<div class="empty-state-card"><h3>No signals found</h3><p>Try a different search phrase.</p></div>`
          }
        </div>
      </section>
    </div>
  `;
}

function buildCreateWorkspaceMarkup(notes: PithyNote[]): string {
  const composer = state.createComposer;
  const selectedSourceNote = getCreateSelectedNote(notes);
  const sourceSummary = state.createOutput?.sourceSummary
    || selectedSourceNote?.summary
    || summarizeCaptureText(composer.rawThought)
    || "Choose a note or paste a rough thought to begin.";
  const previewSlides =
    state.createOutput?.slides ?? buildCreatePreviewSlides(selectedSourceNote, composer.rawThought);
  const previewTitle = state.createOutput?.title ?? buildCreatePreviewTitle(composer.platform);
  const platformLabel = formatCapturePlatformLabel(composer.platform);

  return `
    <div class="create-workspace">
      <header class="section-header create-header">
        <div>
          <h2>Pithy Signal Studio</h2>
        </div>
      </header>

      <section class="create-shell-card create-workspace-shell">
        <section class="capture-composer create-composer-panel">
          <div class="capture-composer-header">
            <div>
              <h3>Choose your source</h3>
              <p>Start from a saved note or paste a fresh reflection.</p>
            </div>
            <button class="composer-clear-button" data-action="clear-create">Clear</button>
          </div>

          ${buildContentReminder()}

          <div class="create-toggle-row">
            ${buildCreateModeButton("existing-note", "Use a saved note")}
            ${buildCreateModeButton("paste", "Paste a rough thought")}
          </div>

          ${
            composer.sourceMode === "existing-note"
              ? `
                <div class="create-note-picker">
                  ${
                    notes.length
                      ? notes
                          .slice(0, 6)
                          .map((note) =>
                            buildCreateSourceCard(note, note.id === composer.sourceNoteId),
                          )
                          .join("")
                      : `<div class="empty-state-card"><h3>No saved notes yet</h3><p>Capture a note first, then come back to Create.</p></div>`
                  }
                </div>
              `
              : `
                <label class="composer-field">
                  <span>Rough thought</span>
                  <textarea data-create-field="rawThought" rows="8" placeholder="Paste a rough thought, transcript fragment, or draft insight here.">${escapeHtml(composer.rawThought)}</textarea>
                </label>
              `
          }

          <div class="capture-composer-grid">
            <label class="composer-field">
              <span>Platform</span>
              <select data-create-field="platform">
                ${buildSelectOptions(
                  [
                    ["none", "None"],
                    ["linkedin", "LinkedIn"],
                    ["instagram", "Instagram"],
                  ],
                  composer.platform,
                )}
              </select>
            </label>

            <label class="composer-field">
              <span>Pithy Palette</span>
              <select data-create-field="palette">
                ${buildSelectOptions(
                  [
                    ["signal", "Signal"],
                    ["editorial", "Editorial"],
                    ["punch", "Punch"],
                  ],
                  composer.palette,
                )}
              </select>
            </label>
          </div>

          <div class="capture-composer-actions">
            <p class="capture-composer-status">${escapeHtml(state.createMessage)}</p>
            <div class="capture-composer-buttons">
              <button class="composer-primary-button" data-action="generate-create-shell" ${state.isGeneratingCreate ? "disabled" : ""}>${state.isGeneratingCreate ? "Generating..." : "Generate Signal Cards"}</button>
            </div>
          </div>
        </section>

        <section class="create-preview-shell create-results-panel">
          <div class="create-preview-header">
            <div class="create-preview-heading">
              <p class="create-preview-kicker">${state.createOutput ? "Generated preview" : "Live preview"}</p>
              <h3>${escapeHtml(previewTitle)}</h3>
              <p class="create-preview-meta">${escapeHtml(platformLabel)} · ${escapeHtml(titleizePalette(composer.palette))} palette</p>
            </div>
          </div>

          ${
            state.createOutput
              ? `
                <div class="create-utility-shell">
                  <div class="create-utility-header">
                    <p class="create-utility-label">Refine direction</p>
                  </div>
                  <div class="create-refinement-row">
                    ${buildCreateRefinementButton("shorter", "Shorter")}
                    ${buildCreateRefinementButton("sharper", "Sharper")}
                    ${buildCreateRefinementButton("more-practical", "More practical")}
                    ${buildCreateRefinementButton("more-punchy", "More punchy")}
                    ${buildCreateRefinementButton("more-professional", "More professional")}
                  </div>
                </div>
              `
              : ""
          }

          <div class="create-preview-summary">
            <h4>Source context</h4>
            <p>${escapeHtml(sourceSummary)}</p>
          </div>

          <div class="create-structure-list">
            ${previewSlides
              .map(
                (slide, index) => `
                  <article class="create-structure-item">
                    <div class="create-structure-item-top">
                      <span class="create-structure-index">0${index + 1}</span>
                      <span class="create-structure-role">${escapeHtml(titleizeCaptureSlideLabel(slide.role))}</span>
                    </div>
                    <h4>${escapeHtml(slide.title)}</h4>
                    <p>${escapeHtml(slide.body)}</p>
                  </article>
                `,
              )
              .join("")}
          </div>

          ${
            state.createOutput
              ? `
                <div class="create-utility-shell">
                  <div class="create-status-row">
                    <div class="create-utility-header">
                      <p class="create-utility-label">Card status</p>
                      <span class="create-status-pill create-status-${escapeAttribute(state.createOutput.status)}">${escapeHtml(titleizeCaptureStatus(state.createOutput.status))}</span>
                    </div>
                    <div class="create-status-actions">
                      ${buildCreateStatusButton("draft")}
                      ${buildCreateStatusButton("ready")}
                      ${buildCreateStatusButton("published")}
                    </div>
                  </div>
                  <div class="create-utility-header">
                    <p class="create-utility-label">Export and manage</p>
                  </div>
                  <div class="create-export-actions">
                    <button class="composer-secondary-button" data-action="export-create-png">Export PNG set</button>
                    <button class="composer-secondary-button" data-action="export-create-pdf">Save PDF</button>
                    <button class="composer-secondary-button" data-action="export-create-caption">Export caption</button>
                    <button class="composer-secondary-button" data-action="duplicate-create-output" data-output-id="${escapeAttribute(state.createOutput.id)}">Duplicate output</button>
                    <button class="composer-secondary-button" data-action="delete-create-output" data-output-id="${escapeAttribute(state.createOutput.id)}">Delete output</button>
                  </div>
                </div>
                <div class="create-preview-summary create-preview-caption">
                  <h4>Caption</h4>
                  <p>${escapeHtml(state.createOutput.caption).replaceAll("\n", "<br />")}</p>
                </div>
              `
              : ""
          }

          ${
            composer.sourceMode === "existing-note" && composer.sourceNoteId
              ? `
                <section class="create-history-shell">
                  <div class="create-history-header">
                    <div>
                      <h4>Saved variants</h4>
                      <p>Reuse or compare prior Signal Card drafts for this source note.</p>
                    </div>
                  </div>
                  ${
                    state.isLoadingCreateHistory
                      ? `<div class="create-history-empty"><p>Loading saved variants...</p></div>`
                      : state.createHistory.length
                        ? `<div class="create-history-list">${state.createHistory
                            .map((output) => buildCreateHistoryCard(output))
                            .join("")}</div>`
                        : `<div class="create-history-empty"><p>No saved variants yet for this note.</p></div>`
                  }
                </section>
              `
              : ""
          }
        </section>
      </section>
    </div>
  `;
}

function buildSourcesViewMarkup(notes: PithyNote[]): string {
  const sourceRecords = getSourceRecords(notes, state.libraryRecords);

  return `
    <div class="sources-view">
      <header class="section-header sources-header">
        <div>
          <h2>Sources</h2>
          <p>Saved references used in briefs, scripts, and deeper research work.</p>
        </div>
      </header>

      <section class="sources-library">
        <div class="sources-library-header">
          <div>
            <h3>Reference library</h3>
          <p>Copy formatted references for your brief, download, or Signal Cards.</p>
          </div>
        </div>

        ${
          sourceRecords.length
            ? `
              <div class="sources-table-shell">
                <div class="sources-table sources-table-head" role="row">
                  <span>Title</span>
                  <span>Reference</span>
                  <span>Copy</span>
                </div>
                <div class="sources-table-body">
                  ${sourceRecords
                .map(
                  (record) => `
                    <div class="sources-table sources-table-row" role="row">
                      <div class="sources-table-title">
                        <strong>${escapeHtml(record.title)}</strong>
                        <span>${escapeHtml(formatLongDate(record.capturedAt))} · ${escapeHtml(titleizeSourceKind(record.sourceKind))}</span>
                      </div>
                      <span class="sources-table-reference">${escapeHtml(record.citation)}</span>
                      <div class="sources-table-actions">
                        <button class="source-action-button" data-action="copy-source-reference" data-reference="${escapeAttribute(record.citation)}" type="button">Copy reference</button>
                      </div>
                    </div>
                  `,
                )
                .join("")}
                </div>
              </div>
            `
            : `<div class="empty-state-card"><h3>No references yet</h3><p>Save a Library record with source links and your references will appear here.</p></div>`
        }
      </section>
    </div>
  `;
}

function buildOutputsLibraryMarkup(): string {
  const outputs = getFilteredOutputs();

  return `
    <div class="sources-view">
      <header class="section-header sources-header">
        <div>
          <h2>Outputs</h2>
          <p>Your saved Signal Card assets across notes, platforms, and variants.</p>
        </div>
      </header>

      <section class="sources-library">
        <div class="sources-library-header">
          <div>
            <h3>Structured Signal Card library</h3>
            <p>Filter, reopen, duplicate, and manage your saved Signal Card variants.</p>
          </div>
        </div>

        <div class="outputs-filter-row">
          <label class="composer-field">
            <select data-outputs-filter="status">
              ${buildSelectOptions(
                [
                  ["all", "All statuses"],
                  ["draft", "Draft"],
                  ["ready", "Ready"],
                  ["published", "Published"],
                ],
                state.outputsFilters.status,
              )}
            </select>
          </label>

          <label class="composer-field">
            <select data-outputs-filter="platform">
              ${buildSelectOptions(
                  [
                    ["all", "All platforms"],
                    ["none", "None"],
                    ["linkedin", "LinkedIn"],
                    ["instagram", "Instagram"],
                  ],
                state.outputsFilters.platform,
              )}
            </select>
          </label>

          <label class="composer-field">
            <select data-outputs-filter="palette">
              ${buildSelectOptions(
                [
                  ["all", "All palettes"],
                  ["signal", "Signal"],
                  ["editorial", "Editorial"],
                  ["punch", "Punch"],
                ],
                state.outputsFilters.palette,
              )}
            </select>
          </label>

          <label class="composer-field">
            <select data-outputs-filter="sort">
              ${buildSelectOptions(
                [
                  ["recent", "Most recent"],
                  ["oldest", "Oldest first"],
                  ["published-first", "Published first"],
                  ["source-title", "Source title"],
                ],
                state.outputsFilters.sort,
              )}
            </select>
          </label>
        </div>

        ${
          state.isLoadingOutputsLibrary
            ? `<div class="empty-state-card"><h3>Loading outputs</h3><p>Gathering your saved Signal Card variants.</p></div>`
            : outputs.length
              ? `<div class="outputs-record-list">${outputs.map((output) => buildOutputsLibraryCard(output)).join("")}</div>`
              : `<div class="empty-state-card"><h3>No outputs yet</h3><p>Generate Signal Cards in Create and they will show up here.</p></div>`
        }
      </section>
    </div>
  `;
}

function buildProfileViewMarkup(): string {
  const accountName = state.session.workspaceUser?.name || "Pithy Notes User";
  const accountEmail = state.session.workspaceUser?.email;
  const accountLabel = state.session.authEnabled ? "Protected workspace" : "Local workspace";
  const storageSummary =
    state.session.workspaceMode === "local"
      ? "Your notes stay on this device."
      : "Your notes stay in this workspace.";
  const aiProvider = state.session.aiProviderLabel ?? "OpenAI";
  const byokProvider = formatUserAiProvider(state.userAiProvider);
  const aiModeLabel =
    state.session.aiCredentialMode === "user"
      ? "Your browser-session key"
      : state.session.aiCredentialMode === "workspace"
        ? "Workspace key"
        : "Local heuristics";
  const aiSummary =
    state.session.aiUsageMode === "local"
      ? "Generation is staying on the local heuristic path."
      : state.session.aiCredentialMode === "user"
        ? `Generation is using your ${aiProvider} key from this browser session and does not save it in the Pithy Notes workspace.`
        : `Generation is using the workspace ${aiProvider} connection.`;
  const includedAiUsage = state.session.includedAiUsage;

  return `
    <div class="sources-view profile-view">
      <header class="section-header sources-header">
        <div>
          <h2>Profile</h2>
          <p>A simple view of your account and workspace settings.</p>
        </div>
      </header>

      <section class="sources-library profile-library">
        <div class="profile-sections">
          <section class="profile-section-card profile-account-card">
            <h4>Account</h4>
            <dl class="profile-definition-list">
              <div><dt>Name</dt><dd>${escapeHtml(accountName)}</dd></div>
              ${accountEmail ? `<div><dt>Email</dt><dd>${escapeHtml(accountEmail)}</dd></div>` : ""}
              <div><dt>Workspace</dt><dd>${accountLabel}</dd></div>
              <div><dt>Status</dt><dd>${state.session.authenticated ? "Signed in" : "Signed out"}</dd></div>
            </dl>
          </section>

          ${
            state.session.byokEnabled
              ? `
                <section class="profile-section-card profile-byok-card">
                  <h4>Bring your own key</h4>
                  <label class="composer-field">
                    <span>AI provider</span>
                    <select data-field="user-ai-provider">
                      ${buildSelectOptions(
                        [
                          ["openai", "OpenAI"],
                          ["claude", "Claude"],
                          ["gemini", "Gemini"],
                        ],
                        state.userAiProvider,
                      )}
                    </select>
                  </label>
                  <label class="composer-field">
                    <span>Your ${escapeHtml(byokProvider)} API key</span>
                    <input
                      type="password"
                      data-field="user-api-key"
                      value="${escapeAttribute(state.userApiKey)}"
                      placeholder="Paste a key for this browser session"
                      autocomplete="off"
                      spellcheck="false"
                    />
                  </label>
                  <label class="composer-field">
                    <span>Embedding API key</span>
                    <input
                      type="password"
                      data-field="embedding-api-key"
                      value="${escapeAttribute(state.embeddingApiKey)}"
                      placeholder="Optional OpenAI key for private-source semantic search"
                      autocomplete="off"
                      spellcheck="false"
                    />
                  </label>
                  <label class="composer-field">
                    <span>ScholarAI API key</span>
                    <input
                      type="password"
                      data-field="scholarai-api-key"
                      value="${escapeAttribute(state.scholarAiApiKey)}"
                      placeholder="Use ScholarAI inside Create"
                      autocomplete="off"
                      spellcheck="false"
                    />
                  </label>
                  <div class="profile-key-grid">
                    <label class="composer-field">
                      <span>Regulations.gov API key</span>
                      <input
                        type="password"
                        data-field="regulations-api-key"
                        value="${escapeAttribute(state.regulationsApiKey)}"
                        placeholder="Ready for regulatory source lookup"
                        autocomplete="off"
                        spellcheck="false"
                      />
                    </label>
                    <label class="composer-field">
                      <span>NVD API key</span>
                      <input
                        type="password"
                        data-field="nvd-api-key"
                        value="${escapeAttribute(state.nvdApiKey)}"
                        placeholder="Ready for vulnerability source lookup"
                        autocomplete="off"
                        spellcheck="false"
                      />
                    </label>
                  </div>
                  <p class="profile-note">Keys are stored only in this browser session and sent only with the request that needs them. Clear them any time to fall back to workspace defaults.</p>
                  <div class="profile-actions">
                    <button class="composer-secondary-button" data-action="clear-user-api-key">Clear generation key</button>
                    <button class="composer-secondary-button" data-action="clear-research-api-keys">Clear research keys</button>
                  </div>
                </section>
              `
              : ""
          }

          <section class="profile-section-card profile-workspace-card">
            <h4>Workspace</h4>
            <dl class="profile-definition-list">
              <div><dt>Storage</dt><dd>${escapeHtml(capitalize(state.session.workspaceMode))}</dd></div>
              <div><dt>AI mode</dt><dd>${escapeHtml(capitalize(state.session.aiUsageMode))}</dd></div>
              <div><dt>Provider</dt><dd>${escapeHtml(aiProvider)}</dd></div>
              <div><dt>Credential</dt><dd>${escapeHtml(aiModeLabel)}</dd></div>
              ${
                includedAiUsage
                  ? `<div><dt>Included AI</dt><dd>${includedAiUsage.used} of ${includedAiUsage.limit} used · ${includedAiUsage.remaining} remaining</dd></div>`
                  : ""
              }
              <div><dt>Privacy</dt><dd>${escapeHtml(storageSummary)}</dd></div>
            </dl>
            <p class="profile-note">${escapeHtml(aiSummary)}</p>
          </section>
        </div>
      </section>
    </div>
  `;
}

function buildLibraryViewMarkup(): string {
  const records = getFilteredLibraryRecords();

  return `
    <div class="sources-view library-view">
      <header class="section-header sources-header">
        <div>
          <h2>Library</h2>
          <p>Saved research records you can search, open, and continue working on.</p>
        </div>
      </header>

      <section class="sources-library library-shell">
        <div class="library-toolbar">
          <label class="composer-field library-search-field">
            <span>Search library</span>
            <input data-library-field="search" value="${escapeAttribute(state.librarySearch)}" placeholder="Search saved titles" />
          </label>
        </div>

        <div class="library-table-shell">
          <div class="library-table library-table-head" role="row">
            <span>Title</span>
            <span>Report</span>
            <span>Script</span>
            <span>Deep Dive</span>
            <span>Updated</span>
            <span>Export</span>
            <span>Delete</span>
          </div>

          ${
            state.isLoadingLibrary
              ? `<div class="empty-state-card"><h3>Loading library</h3><p>Gathering saved research records.</p></div>`
              : records.length
                ? `<div class="library-table-body">${records.map((record) => buildLibraryRow(record)).join("")}</div>`
                : `<div class="empty-state-card"><h3>Your library is empty</h3><p>Create your first signal, then come back here to reopen and continue the work.</p></div>`
          }
        </div>
      </section>
    </div>
  `;
}

function buildResearchPipelineMarkup(): string {
  const composer = state.researchComposer;
  const assessmentComposer = state.assessmentComposer;
  const workflow: ReportWorkflow = "intelligence";
  const isAssessment = false;
  const brief = state.researchBrief;
  const assessmentReport = state.assessmentReport;
  const script = state.researchScript;
  const deepDive = state.researchDeepDive;
  const briefLabel = isAssessment ? "Assessment Report" : getResearchBriefLabel(composer.mode);
  const briefButtonLabel = isAssessment ? "Create Assessment Report" : "Create Intelligence Brief";
  const activeRecord = state.activeLibraryRecordId
    ? state.libraryRecords.find((record) => record.id === state.activeLibraryRecordId) ?? null
    : null;
  const workspaceTitle = composer.title.trim() || activeRecord?.title || "Untitled signal";
  const workspaceModeLabel = activeRecord ? "Saved Library Record" : "New Signal Draft";

  return `
    <div class="research-view">
      <section class="research-composer">
        <div class="research-action-bar">
          <div class="research-action-copy">
            <span>${escapeHtml(workspaceModeLabel)}</span>
            <strong>${escapeHtml(workspaceTitle)}</strong>
            <p>${activeRecord ? "You are editing a saved signal. Start a new one when you are ready for the next topic." : "Start here, then save the signal when the brief is ready."}</p>
          </div>
          <div class="research-action-buttons">
            <button class="composer-primary-button research-action-primary" data-action="add-new-research-signal">
              New Signal
            </button>
            <button class="composer-secondary-button" data-action="save-library-record">
              ${state.activeLibraryRecordId ? "Update" : "Save"}
            </button>
            <button class="composer-secondary-button" data-action="clear-research-workspace">
              Clear
            </button>
            <button class="composer-secondary-button" data-action="download-current-research-record">
              Download
            </button>
          </div>
        </div>

        <div class="capture-composer-grid">
          <label class="composer-field composer-field-wide">
            <span>Report Type</span>
            <select data-research-field="workflow">
              ${buildSelectOptions(
                [
                  ["intelligence", "Research / Intelligence Brief"],
                ],
                workflow,
              )}
            </select>
          </label>
        </div>
        ${
          isAssessment
            ? buildAssessmentComposerFields(assessmentComposer)
            : `
        <div class="capture-composer-grid">
          <label class="composer-field composer-field-wide">
            <span>Title</span>
            <input data-research-field="title" value="${escapeAttribute(composer.title)}" placeholder="A clear title for this signal" />
          </label>

          <label class="composer-field composer-field-wide research-sources-field">
            <span>Sources</span>
            <div class="composer-field-row">
              <input data-research-field="sourceUrl" value="${escapeAttribute(composer.sourceUrl)}" placeholder="Paste a source URL" />
              <button class="composer-secondary-button composer-inline-button" data-action="add-research-source" type="button">Add</button>
              ${
                composer.sourceUrl.trim()
                  ? `<button class="composer-secondary-button composer-inline-button composer-clear-inline-button" data-action="clear-research-source-input" type="button">Clear</button>`
                  : ""
              }
            </div>
            ${
              composer.sourceUrls.length
                ? `
                  <div class="research-source-list">
                    ${composer.sourceUrls
                      .map(
                        (sourceUrl, index) => `
                          <div class="research-source-row">
                            <div class="research-source-copy">
                              <strong>Source ${index + 1}</strong>
                              <a class="research-source-link" href="${escapeAttribute(sourceUrl)}" target="_blank" rel="noreferrer">${escapeHtml(sourceUrl)}</a>
                            </div>
                            <button class="research-source-remove" data-action="remove-research-source" data-source-index="${index}" type="button" aria-label="Delete source ${index + 1}">Delete</button>
                          </div>
                        `,
                      )
                      .join("")}
                  </div>
                `
                : ""
            }
          </label>

          <label class="composer-field composer-field-wide">
            <span>Research Text</span>
            <textarea data-research-field="paperText" placeholder="Paste the abstract, key sections, article text, notes, or source material here">${escapeHtml(composer.paperText)}</textarea>
          </label>

          <div class="composer-field composer-field-wide document-upload-panel">
            <div class="document-upload-copy">
              <span>Documents</span>
              <p>Bring your own documents. Add multiple text, Markdown, CSV, JSON, HTML, or log files and Studio will add them to the research text locally.</p>
            </div>
            <label class="document-upload-dropzone">
              <input
                type="file"
                data-action="upload-research-documents"
                multiple
                accept=".txt,.md,.markdown,.csv,.json,.html,.htm,.xml,.log,text/*,application/json"
              />
              <strong>Upload documents</strong>
              <small>Processed in this browser before you create a brief.</small>
            </label>
            ${
              composer.documents.length
                ? `
                  <div class="research-document-list">
                    ${composer.documents
                      .map(
                        (document) => `
                          <div class="research-document-row">
                            <div>
                              <strong>${escapeHtml(document.name)}</strong>
                              <span>${escapeHtml(formatFileSize(document.size))} · ${escapeHtml(formatLongDate(document.addedAt))}</span>
                            </div>
                            <button class="research-source-remove" data-action="remove-research-document" data-document-id="${escapeAttribute(document.id)}" type="button">Remove</button>
                          </div>
                        `,
                      )
                      .join("")}
                  </div>
                `
                : ""
            }
          </div>

          ${buildPrivateSourceLibraryPanel()}
        </div>
        `
        }

        <div class="capture-composer-actions research-composer-actions">
          <p class="capture-composer-status research-composer-status">${
            state.researchMessage
              ? escapeHtml(state.researchMessage)
              : isAssessment
                ? "Create an assessment report to strengthen and communicate your professional value."
                : "Create an intelligence brief first. Then create a script, deep dive, or Signal Cards from the saved record."
          }</p>
          <div class="capture-composer-buttons research-composer-buttons">
            <button class="composer-primary-button composer-hero-button" data-action="generate-research-brief" ${state.isGeneratingResearchBrief ? "disabled" : ""}>
              ${state.isGeneratingResearchBrief ? "Creating..." : escapeHtml(briefButtonLabel)}
            </button>
            <button class="composer-secondary-button composer-output-button" data-action="generate-research-script" ${isAssessment || state.isGeneratingResearchScript || !brief ? "disabled" : ""}>
              ${state.isGeneratingResearchScript ? "Writing..." : "Create script"}
            </button>
            <button class="composer-secondary-button composer-output-button" data-action="generate-research-deep-dive" ${isAssessment || state.isGeneratingResearchDeepDive || !brief ? "disabled" : ""}>
              ${state.isGeneratingResearchDeepDive ? "Building..." : "Create deep dive"}
            </button>
            <button class="composer-secondary-button composer-output-button" data-action="create-signal-cards-from-research" ${isAssessment || (!brief && !composer.paperText.trim()) || state.isGeneratingCreate ? "disabled" : ""}>
              ${state.isGeneratingCreate ? "Generating cards..." : "Create Signal Cards"}
            </button>
          </div>
        </div>

      </section>

      <div class="research-grid">
        <section class="research-card">
          <div class="research-card-header">
            <h3>${escapeHtml(briefLabel)}</h3>
            <span class="generated-pill">${isAssessment ? (assessmentReport ? "Ready" : "Not generated yet") : (brief ? "Ready" : "Not generated yet")}</span>
          </div>
          ${
            isAssessment
              ? assessmentReport
                ? buildAssessmentReportMarkup(assessmentReport)
                : `<p class="research-empty-copy">Create an assessment report from your own non-confidential reflection or example.</p>`
              : brief
                ? buildResearchBriefMarkup(brief, composer.mode)
                : `<p class="research-empty-copy">Create an intelligence brief to turn the source material into a clear, decision-ready signal.</p>`
          }
        </section>

        <section class="research-card">
          <div class="research-card-header">
            <h3>Script</h3>
            <span class="generated-pill">${isAssessment ? "Available for intelligence briefs" : (script ? "Script ready" : "Waiting for brief")}</span>
          </div>
          ${
            !isAssessment && script
              ? buildResearchScriptMarkup(script)
              : `<p class="research-empty-copy">${isAssessment ? "Assessment reports stay focused on professional value reporting and do not generate briefing scripts." : "Create a script after the intelligence brief is ready."}</p>`
          }
        </section>

        <section class="research-card research-card-wide">
          <div class="research-card-header">
            <h3>Deep Dive</h3>
            <div class="research-card-actions">
              <span class="generated-pill">${isAssessment ? "Available for intelligence briefs" : (deepDive ? "Long-form script ready" : "Long-form script")}</span>
              ${!isAssessment && deepDive ? `<button class="download-button" data-action="download-deep-dive" aria-label="Download deep dive text">${buildDownloadIcon()}</button>` : ""}
            </div>
          </div>
          ${
            !isAssessment && deepDive
              ? buildResearchDeepDiveMarkup(deepDive)
              : `<p class="research-empty-copy">${isAssessment ? "Assessment reports end with practical next moves and stronger language instead of a long-form external briefing script." : "Use this for a longer-form script, roughly 1,200 to 2,200 words, for reading or narration."}</p>`
          }
        </section>
      </div>
    </div>
  `;
}

function buildPrivateSourceLibraryPanel(): string {
  const composer = state.researchComposer;
  return `
    <section class="composer-field composer-field-wide private-source-panel">
      <div class="document-upload-copy">
        <span>Private Source Library</span>
        <p>Save uploaded documents to this private Studio, then search and attach relevant excerpts to a new brief.</p>
      </div>
      <div class="private-source-actions">
        <button class="composer-secondary-button" data-action="save-documents-to-private-sources" type="button" ${composer.documents.length ? "" : "disabled"}>
          Save uploaded documents
        </button>
        <span>${escapeHtml(state.privateSourceMessage)}</span>
      </div>
      <div class="composer-field-row">
        <input data-research-field="privateSourceQuery" value="${escapeAttribute(composer.privateSourceQuery)}" placeholder="Search your private sources" />
        <button class="composer-secondary-button composer-inline-button" data-action="search-private-sources" type="button">
          ${state.isSearchingPrivateSources ? "Searching..." : "Search"}
        </button>
      </div>
      ${
        state.privateSourceResults.length
          ? `
            <div class="private-source-result-list">
              ${state.privateSourceResults
                .map(
                  (result, index) => `
                    <article class="private-source-result">
                      <div>
                        <strong>${escapeHtml(result.title)}</strong>
                        <span>${escapeHtml(result.filename || result.sourceUrl || `Chunk ${result.chunkIndex + 1}`)}</span>
                      </div>
                      <p>${escapeHtml(excerpt(result.chunkText, 280))}</p>
                      <button class="research-source-remove" data-action="attach-private-source-result" data-result-index="${index}" type="button">Attach excerpt</button>
                    </article>
                  `,
                )
                .join("")}
            </div>
          `
          : state.privateSources.length
            ? `<p class="private-source-hint">${escapeHtml(state.privateSources.length.toString())} private source${state.privateSources.length === 1 ? "" : "s"} saved. Search to retrieve excerpts.</p>`
            : `<p class="private-source-hint">No private sources saved yet. Upload documents above, then save them here.</p>`
      }
    </section>
  `;
}

function buildScholarAiPanel(): string {
  const composer = state.researchComposer;
  return `
    <section class="composer-field composer-field-wide research-lookup-panel">
      <div class="document-upload-copy">
        <span>Find authoritative papers</span>
        <p>Search ScholarAI, then attach the useful paper summary to this signal.</p>
      </div>
      <div class="composer-field-row">
        <input data-research-field="scholarQuery" value="${escapeAttribute(composer.scholarQuery)}" placeholder="Search peer-reviewed papers with ScholarAI" />
        <button class="composer-secondary-button composer-inline-button" data-action="search-scholarai" type="button">
          ${state.isSearchingScholarAi ? "Searching..." : "Search"}
        </button>
      </div>
      <p class="private-source-hint">${escapeHtml(state.scholarAiMessage)}</p>
      ${
        state.scholarAiResults.length
          ? `
            <div class="private-source-result-list research-lookup-result-list">
              ${state.scholarAiResults
                .map(
                  (paper, index) => `
                    <article class="private-source-result research-lookup-result">
                      <div>
                        <strong>${escapeHtml(paper.title)}</strong>
                        <span>${escapeHtml(formatScholarAiMeta(paper))}</span>
                      </div>
                      <p>${escapeHtml(excerpt(paper.answer || paper.abstract || "No abstract returned.", 320))}</p>
                      <button class="research-source-remove" data-action="attach-scholarai-result" data-scholar-index="${index}" type="button">Attach paper</button>
                    </article>
                  `,
                )
                .join("")}
            </div>
          `
          : ""
      }
    </section>
  `;
}

function buildAssessmentComposerFields(composer: AssessmentComposerState): string {
  return `
    <div class="capture-composer-grid">
      <label class="composer-field composer-field-wide">
        <span>Reflection or Example</span>
        <textarea data-assessment-field="workExample" placeholder="Describe your own non-confidential reflection or example.">${escapeHtml(composer.workExample)}</textarea>
      </label>
      <label class="composer-field composer-field-wide">
        <span>What You Did</span>
        <textarea data-assessment-field="whatUserDid" placeholder="What did you do?">${escapeHtml(composer.whatUserDid ?? "")}</textarea>
      </label>
      <label class="composer-field composer-field-wide">
        <span>How You Did It</span>
        <textarea data-assessment-field="howUserDidIt" placeholder="How did you do it?">${escapeHtml(composer.howUserDidIt ?? "")}</textarea>
      </label>
      <label class="composer-field">
        <span>Stakeholders Involved</span>
        <input data-assessment-field="stakeholdersInvolved" value="${escapeAttribute(composer.stakeholdersInvolved ?? "")}" placeholder="Leadership, cross-functional partners, customers" />
      </label>
      <label class="composer-field">
        <span>Business Impact</span>
        <input data-assessment-field="businessImpact" value="${escapeAttribute(composer.businessImpact ?? "")}" placeholder="Operational, customer, risk, leadership, or strategic impact" />
      </label>
      <label class="composer-field">
        <span>AI Used</span>
        <input data-assessment-field="aiUsed" value="${escapeAttribute(composer.aiUsed ?? "")}" placeholder="How AI helped, if applicable" />
      </label>
      <label class="composer-field">
        <span>Current Status</span>
        <input data-assessment-field="currentStatus" value="${escapeAttribute(composer.currentStatus ?? "")}" placeholder="Complete, emerging impact, early signal, in progress" />
      </label>
      <label class="composer-field composer-field-wide">
        <span>Support Available</span>
        <textarea data-assessment-field="evidenceAvailable" placeholder="What outcomes, context, or examples do you already have?">${escapeHtml(composer.evidenceAvailable ?? "")}</textarea>
      </label>
      <label class="composer-field composer-field-wide">
        <span>Desired Use Case</span>
        <textarea data-assessment-field="desiredUseCase" placeholder="Performance review, leadership update, portfolio, next move" >${escapeHtml(composer.desiredUseCase ?? "")}</textarea>
      </label>
    </div>
  `;
}

function buildAssessmentReportMarkup(report: PithySignalAssessmentReport): string {
  return `
    <div class="research-brief-sections">
      ${buildResearchSection("Value Signal Summary", `<p>${escapeHtml(report.valueSignalSummary)}</p>`)}
      ${buildResearchSection(
        "OMAII Signal Classification",
        buildResearchList([
          `Observation: ${report.omaiiClassification.observation}`,
          `Meaning: ${report.omaiiClassification.meaning}`,
          `Action: ${report.omaiiClassification.action}`,
          `Impact: ${report.omaiiClassification.impact}`,
          `Intelligence: ${report.omaiiClassification.intelligence}`,
        ]),
      )}
      ${buildResearchSection("Why This Matters", `<p>${escapeHtml(report.whyThisMatters)}</p>`)}
      ${buildResearchSection(
        "Actionable Insights",
        `<p class="private-source-hint">Practical next steps to strengthen and communicate your professional value.</p>${buildResearchList(report.actionableInsights)}`,
      )}
      ${buildResearchSection(
        "Stronger Language",
        buildResearchList([
          `Concise version: ${report.strongerLanguage.conciseVersion}`,
          `Performance review version: ${report.strongerLanguage.performanceReviewVersion}`,
          `Leadership update version: ${report.strongerLanguage.leadershipUpdateVersion}`,
        ]),
      )}
      ${buildResearchSection("What to Save Next", buildResearchList(report.evidenceToCapture))}
      ${buildResearchSection("Next Move", `<p>${escapeHtml(report.nextMove)}</p>`)}
    </div>
  `;
}

function buildResearchBriefMarkup(
  brief: GovernanceResearchBrief,
  mode: ResearchBriefMode,
): string {
  const references = getCurrentResearchReferences();
  const isGovernanceMode = mode === "governance";
  return `
    <div class="research-brief-sections">
      ${buildResearchSection("Signal Summary", `<p>${escapeHtml(brief.signalSummary)}</p>`)}
      ${buildResearchSection("Pithy Insight", `<p>${escapeHtml(brief.pithyInsight)}</p>`)}
      ${buildResearchSection("Why This Matters", `<p>${escapeHtml(brief.whyThisMatters)}</p>`)}
      ${buildResearchSection(
        "Action Triggers",
        `<p class="private-source-hint">Practical actions to consider based on this ${isGovernanceMode ? "governance, market, or risk" : "research, market, or topic"} signal.</p>${buildResearchList(brief.actionTriggers)}`,
      )}
      ${buildResearchSection(
        isGovernanceMode ? "Governance Implications" : "Operating Implications",
        `<p>${escapeHtml(brief.governanceImplications)}</p>`,
      )}
      ${buildResearchSection(isGovernanceMode ? "TPRM Implications" : "Dependency / External Implications", `<p>${escapeHtml(brief.tprmImplications)}</p>`)}
      ${buildResearchSection("Repeat Pattern Tag", `<p>${escapeHtml(brief.repeatPatternTag)}</p>`)}
      ${buildResearchSection(
        "Signal Strength",
        `<p>${escapeHtml(String(brief.signalStrength.rating))} / 5</p><p>${escapeHtml(brief.signalStrength.rationale)}</p>`,
      )}
      ${buildResearchSection(
        "Build vs. Ignore",
        `<p>${escapeHtml(brief.buildVsIgnore.classification)}</p><p>${escapeHtml(brief.buildVsIgnore.rationale)}</p>`,
      )}
      ${buildResearchSection(
        "Suggested Pithy Notes Asset",
        `<p>${escapeHtml(brief.suggestedPithyNotesAsset)}</p>`,
      )}
      ${buildResearchSection(
        "Source Notes",
        [
          "<div>",
          "<strong>Sourced facts</strong>",
          buildResearchList(brief.sourceNotes.sourcedFacts),
          "<strong>Reasonable analysis</strong>",
          buildResearchList(brief.sourceNotes.reasonableAnalysis),
          "<strong>Original Pithy Signal interpretation</strong>",
          buildResearchList(brief.sourceNotes.originalPithySignalInterpretation),
          "</div>",
        ].join(""),
      )}
      ${
        references.length
          ? buildResearchSection("References", buildResearchList(references))
          : ""
      }
    </div>
  `;
}

function buildResearchScriptMarkup(script: AvatarScript): string {
  return `
    <div class="research-script-sections">
      ${buildResearchSection("Episode title", `<p>${escapeHtml(script.title)}</p>`)}
      ${buildResearchSection("Hook", `<p>${escapeHtml(script.hook)}</p>`)}
      ${buildResearchSection("Sections", buildResearchList(script.sections))}
      ${buildResearchSection("Close", `<p>${escapeHtml(script.close)}</p>`)}
      ${buildResearchSection("Full script", `<pre class="research-script-block">${escapeHtml(script.fullScript)}</pre>`)}
    </div>
  `;
}

function getResearchBriefLabel(_mode: ResearchBriefMode): string {
  return "Intelligence Brief";
}

function buildResearchDeepDiveMarkup(deepDive: DeepDiveScript): string {
  return `
    <div class="research-deep-dive-sections">
      ${buildResearchSection("Title", `<p>${escapeHtml(deepDive.title)}</p>`)}
      ${buildResearchSection("Executive framing", `<p>${escapeHtml(deepDive.executiveFraming)}</p>`)}
      ${deepDive.sections
        .map((section) =>
          buildResearchSection(section.heading, `<p>${escapeHtml(section.body)}</p>`),
        )
        .join("")}
      ${buildResearchSection("Practical takeaways", buildResearchList(deepDive.practicalTakeaways))}
      ${buildResearchSection("Closing perspective", `<p>${escapeHtml(deepDive.closingPerspective)}</p>`)}
      ${buildResearchSection("Full text", `<pre class="research-script-block">${escapeHtml(deepDive.fullText)}</pre>`)}
    </div>
  `;
}

function buildResearchSection(title: string, body: string): string {
  return `
    <section class="research-section">
      <h4>${escapeHtml(title)}</h4>
      ${body}
    </section>
  `;
}

function buildResearchList(items: string[]): string {
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`;
}

function formatScholarAiMeta(paper: ScholarAiPaperResult): string {
  const parts = [
    paper.authors.length ? paper.authors.slice(0, 3).join(", ") : "",
    paper.publicationDate ? paper.publicationDate.slice(0, 4) : "",
    typeof paper.citedByCount === "number" ? `${paper.citedByCount} citations` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "ScholarAI result";
}

function bindEvents(): void {
  if (!root) {
    return;
  }

  const authForm = root.querySelector<HTMLFormElement>("#auth-form");
  const authPasswordInput = root.querySelector<HTMLInputElement>("#auth-password");
  authPasswordInput?.addEventListener("input", () => {
    state = { ...state, authPassword: authPasswordInput.value, authError: "" };
  });
  authForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitLogin();
  });

  const searchInput = root.querySelector<HTMLInputElement>("#search-input");
  searchInput?.addEventListener("input", () => {
    state = { ...state, search: searchInput.value };
    render();
  });

  root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "[data-capture-field]",
    )
    .forEach((field) => {
      field.addEventListener("input", () => {
        updateCaptureComposerField(field.dataset.captureField ?? "", field.value);
      });
      field.addEventListener("change", () => {
        updateCaptureComposerField(field.dataset.captureField ?? "", field.value);
      });
    });

  root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-research-field]")
    .forEach((field) => {
      field.addEventListener("input", () => {
        updateResearchComposerField(field.dataset.researchField ?? "", field.value, {
          renderAfterUpdate: false,
        });
      });
      if (field instanceof HTMLInputElement && field.dataset.researchField === "sourceUrl") {
        field.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            addResearchSource();
          }
        });
      }
      if (
        field instanceof HTMLInputElement
        && field.dataset.researchField === "privateSourceQuery"
      ) {
        field.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void searchPrivateSourcesForComposer();
          }
        });
      }
      if (field instanceof HTMLInputElement && field.dataset.researchField === "scholarQuery") {
        field.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void searchScholarAiForComposer();
          }
        });
      }
    });

  root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-assessment-field]")
    .forEach((field) => {
      field.addEventListener("input", () => {
        updateAssessmentComposerField(field.dataset.assessmentField ?? "", field.value);
      });
    });

  root
    .querySelectorAll<HTMLInputElement>("[data-action='upload-research-documents']")
    .forEach((field) => {
      field.addEventListener("change", () => {
        void uploadResearchDocuments(field.files);
        field.value = "";
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='remove-research-document']").forEach((button) => {
    button.addEventListener("click", () => {
      const documentId = button.dataset.documentId;
      if (documentId) {
        removeResearchDocument(documentId);
      }
    });
  });

  root
    .querySelectorAll<HTMLElement>("[data-action='save-documents-to-private-sources']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void saveResearchDocumentsToPrivateSources();
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='search-private-sources']").forEach((button) => {
    button.addEventListener("click", () => {
      void searchPrivateSourcesForComposer();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='search-scholarai']").forEach((button) => {
    button.addEventListener("click", () => {
      void searchScholarAiForComposer();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='attach-scholarai-result']").forEach((button) => {
    button.addEventListener("click", () => {
      const resultIndex = Number(button.dataset.scholarIndex ?? "-1");
      if (Number.isNaN(resultIndex) || resultIndex < 0) {
        return;
      }
      attachScholarAiResult(resultIndex);
    });
  });

  root
    .querySelectorAll<HTMLElement>("[data-action='attach-private-source-result']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const resultIndex = Number(button.dataset.resultIndex ?? "-1");
        if (Number.isNaN(resultIndex) || resultIndex < 0) {
          return;
        }
        attachPrivateSourceResult(resultIndex);
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='add-research-source']").forEach((button) => {
    button.addEventListener("click", () => {
      addResearchSource();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='clear-research-source-input']").forEach((button) => {
    button.addEventListener("click", () => {
      clearResearchSourceInput();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='remove-research-source']").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceIndex = Number(button.dataset.sourceIndex ?? "-1");
      if (Number.isNaN(sourceIndex) || sourceIndex < 0) {
        return;
      }
      removeResearchSource(sourceIndex);
    });
  });

  root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "[data-create-field]",
    )
    .forEach((field) => {
      field.addEventListener("input", () => {
        updateCreateComposerField(field.dataset.createField ?? "", field.value);
      });
      field.addEventListener("change", () => {
        updateCreateComposerField(field.dataset.createField ?? "", field.value);
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='draft-capture']").forEach((button) => {
    button.addEventListener("click", () => {
      void draftCaptureInApp();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='save-capture']").forEach((button) => {
    button.addEventListener("click", () => {
      void saveCaptureInApp();
    });
  });

  root
    .querySelectorAll<HTMLElement>("[data-action='generate-research-brief']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void generateResearchBrief();
      });
    });

  root
    .querySelectorAll<HTMLElement>("[data-action='generate-research-script']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void generateResearchScript();
      });
    });

  root
    .querySelectorAll<HTMLElement>("[data-action='generate-research-deep-dive']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void generateResearchDeepDive();
      });
    });

  root
    .querySelectorAll<HTMLElement>("[data-action='create-signal-cards-from-research']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void createSignalCardsFromResearch();
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='download-deep-dive']").forEach((button) => {
    button.addEventListener("click", () => {
      downloadResearchDeepDive();
    });
  });

  root
    .querySelectorAll<HTMLElement>("[data-action='download-current-research-record']")
    .forEach((button) => {
      button.addEventListener("click", () => {
        downloadCurrentResearchRecord();
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='clear-capture']").forEach((button) => {
    button.addEventListener("click", () => {
      state = {
        ...state,
        captureComposer: createEmptyCaptureComposer(),
        captureMessage: "Capture composer reset.",
      };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='clear-create']").forEach((button) => {
    button.addEventListener("click", () => {
      state = {
        ...state,
        createComposer: createEmptyCreateComposer(),
        createPublishReceipt: createEmptyPublishReceipt(),
        createOutput: null,
        createHistory: [],
        isLoadingCreateHistory: false,
        createMessage: "Create workspace reset.",
      };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='generate-create-shell']").forEach((button) => {
    button.addEventListener("click", () => {
      void generateCreateOutput();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='export-create-png']").forEach((button) => {
    button.addEventListener("click", () => {
      void exportCreatePngSet();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='export-create-pdf']").forEach((button) => {
    button.addEventListener("click", () => {
      exportCreatePdf();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='export-create-caption']").forEach((button) => {
    button.addEventListener("click", () => {
      exportCreateCaption();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='refine-create']").forEach((button) => {
    button.addEventListener("click", () => {
      const refinementMode = button.dataset.refinementMode as CaptureRefinementMode | undefined;
      if (!refinementMode) {
        return;
      }
      void generateCreateOutput(refinementMode);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='load-create-output']").forEach((button) => {
    button.addEventListener("click", () => {
      const outputId = button.dataset.outputId ?? "";
      if (!outputId) {
        return;
      }
      loadCreateOutputFromHistory(outputId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='branch-create-output']").forEach((button) => {
    button.addEventListener("click", () => {
      const outputId = button.dataset.outputId ?? "";
      if (!outputId) {
        return;
      }
      void branchCreateOutputFromHistory(outputId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='open-output-library-item']").forEach((button) => {
    button.addEventListener("click", () => {
      const outputId = button.dataset.outputId ?? "";
      if (!outputId) {
        return;
      }
      openOutputLibraryItem(outputId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='delete-create-output']").forEach((button) => {
    button.addEventListener("click", () => {
      const outputId = button.dataset.outputId ?? state.createOutput?.id ?? "";
      if (!outputId) {
        return;
      }
      void deleteCreateOutput(outputId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='duplicate-create-output']").forEach((button) => {
    button.addEventListener("click", () => {
      const outputId = button.dataset.outputId ?? state.createOutput?.id ?? "";
      if (!outputId) {
        return;
      }
      void duplicateCreateOutput(outputId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='set-create-status']").forEach((button) => {
    button.addEventListener("click", () => {
      const status = button.dataset.outputStatus as CaptureOutputStatus | undefined;
      if (!status) {
        return;
      }
      void updateCreateOutputStatus(status);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='set-create-mode']").forEach((button) => {
    button.addEventListener("click", () => {
      const mode =
        (button.dataset.createMode as CreateComposerState["sourceMode"] | undefined) ??
        "existing-note";
      state = {
        ...state,
        createComposer: {
          ...state.createComposer,
          sourceMode: mode,
        },
        createPublishReceipt: createEmptyPublishReceipt(),
        createOutput: null,
        createHistory: mode === "existing-note" ? state.createHistory : [],
        isLoadingCreateHistory: false,
        createMessage:
          mode === "existing-note"
            ? "Choose a saved note to shape into Signal Cards."
            : "Paste a rough thought to shape into Signal Cards.",
      };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='open-create']").forEach((button) => {
    button.addEventListener("click", () => {
      openCreateWorkspace(button.dataset.noteId ?? state.selectedNoteId ?? "");
    });
  });

  root.querySelectorAll<HTMLElement>("[data-create-source-note-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const noteId = button.dataset.createSourceNoteId ?? "";
      if (!noteId) {
        return;
      }
      state = {
        ...state,
        createComposer: {
          ...state.createComposer,
          sourceMode: "existing-note",
          sourceNoteId: noteId,
        },
        createPublishReceipt: createEmptyPublishReceipt(),
        createOutput: null,
        createHistory: [],
        isLoadingCreateHistory: true,
        createMessage: "Source note selected.",
      };
      render();
      void loadCreateHistory(noteId);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-note-id]").forEach((button) => {
    button.addEventListener("click", (event) => {
      const target = event.target;
      if (
        target instanceof Element
        && target !== button
        && (target.closest("[data-action]") || target.closest("a"))
      ) {
        return;
      }
      state = { ...state, selectedNoteId: button.dataset.noteId ?? null };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-nav]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const nextView = (button.dataset.nav as SignalView) ?? "capture";
      navigateToView(nextView);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='signout']").forEach((button) => {
    button.addEventListener("click", () => {
      void signOut();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='clear-user-api-key']").forEach((button) => {
    button.addEventListener("click", () => {
      void updateUserApiKey("");
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='clear-research-api-keys']").forEach((button) => {
    button.addEventListener("click", () => {
      updateResearchApiKeys({
        embeddingApiKey: "",
        scholarAiApiKey: "",
        regulationsApiKey: "",
        nvdApiKey: "",
      });
    });
  });

  root.querySelectorAll<HTMLInputElement>("[data-field='user-api-key']").forEach((field) => {
    field.addEventListener("change", () => {
      void updateUserApiKey(field.value);
    });
  });

  root
    .querySelectorAll<HTMLInputElement>(
      "[data-field='embedding-api-key'], [data-field='scholarai-api-key'], [data-field='regulations-api-key'], [data-field='nvd-api-key']",
    )
    .forEach((field) => {
      field.addEventListener("change", () => {
        updateResearchApiKeyField(field.dataset.field ?? "", field.value);
      });
    });

  root.querySelectorAll<HTMLSelectElement>("[data-field='user-ai-provider']").forEach((field) => {
    field.addEventListener("change", () => {
      void updateUserAiProvider(normalizeUserAiProvider(field.value));
    });
  });

  root.querySelectorAll<HTMLInputElement>("[data-library-field='search']").forEach((field) => {
    field.addEventListener("input", () => {
      state = { ...state, librarySearch: field.value };
      render();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='save-library-record']").forEach((button) => {
    button.addEventListener("click", () => {
      void saveCurrentLibraryRecord();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='add-new-research-signal']").forEach((button) => {
    button.addEventListener("click", () => {
      startNewResearchSignal();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='clear-research-workspace']").forEach((button) => {
    button.addEventListener("click", () => {
      clearResearchWorkspace();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='open-library-record']").forEach((button) => {
    button.addEventListener("click", () => {
      const recordId = button.dataset.recordId ?? "";
      if (recordId) {
        void openLibraryRecord(recordId);
      }
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='download-library-record']").forEach((button) => {
    button.addEventListener("click", () => {
      const recordId = button.dataset.recordId ?? "";
      if (recordId) {
        downloadLibraryRecord(recordId);
      }
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='delete-library-record']").forEach((button) => {
    button.addEventListener("click", () => {
      const recordId = button.dataset.recordId ?? "";
      if (recordId) {
        void deleteLibraryRecordById(recordId);
      }
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='copy-source-reference']").forEach((button) => {
    button.addEventListener("click", () => {
      const reference = button.dataset.reference ?? "";
      if (reference) {
        void copyReferenceToClipboard(reference);
      }
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='open-brief']").forEach((button) => {
    button.addEventListener("click", () => {
      navigateToView("brief");
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='close-brief']").forEach((button) => {
    button.addEventListener("click", () => {
      navigateToView("capture");
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='download-brief']").forEach((button) => {
    button.addEventListener("click", () => {
      downloadWeeklyBrief();
    });
  });

  root
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-brief-filter]")
    .forEach((field) => {
      field.addEventListener("change", () => {
        updateBriefFilterField(field.dataset.briefFilter ?? "", field.value);
        void loadBriefAiSummary(true);
      });
    });

  root.querySelectorAll<HTMLElement>("[data-action='reset-brief-filters']").forEach((button) => {
    button.addEventListener("click", () => {
      state = {
        ...state,
        briefFilters: {
          category: "",
          tag: "",
          fromDate: "",
          toDate: "",
        },
        briefAiSummary: null,
        briefAiSummaryScopeKey: "",
      };
      render();
      void loadBriefAiSummary(true);
    });
  });

  root.querySelectorAll<HTMLElement>("[data-action='delete-note']").forEach((button) => {
    button.addEventListener("click", () => {
      const noteId = button.dataset.noteId ?? "";
      if (noteId) {
        void deleteCurrentNote(noteId);
      }
    });
  });

  root
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-outputs-filter]")
    .forEach((field) => {
      field.addEventListener("change", () => {
        updateOutputsFilterField(field.dataset.outputsFilter ?? "", field.value);
      });
    });
}

async function submitLogin(): Promise<void> {
  if (!state.authPassword.trim()) {
    state = { ...state, authError: "Enter the app password to continue." };
    render();
    return;
  }

  state = { ...state, isSubmittingAuth: true, authError: "" };
  render();

  const response = await fetch("/api/session/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password: state.authPassword }),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    state = {
      ...state,
      isSubmittingAuth: false,
      authError: payload?.error ?? "Sign-in failed.",
    };
    render();
    return;
  }

  state = {
    ...state,
    session: { ...state.session, authEnabled: true, authenticated: true },
    authPassword: "",
    authError: "",
    isSubmittingAuth: false,
  };
  render();
  await loadDashboard();
  await loadLibraryRecords();
  await loadPrivateSources();
}

async function signOut(): Promise<void> {
  await fetch("/api/session/logout", { method: "POST" });
  const session = await requestJson<SessionState>("/api/session");
  state = {
    ...state,
    notes: [],
    brief: null,
    privateSources: [],
    privateSourceResults: [],
    selectedNoteId: null,
    search: "",
    authPassword: "",
    authError: "",
    session,
    status:
      session.authEnabled && !session.authenticated
        ? "Enter your Pithy Signal password."
        : "Workspace ready.",
  };
  render();
  if (!session.authEnabled || session.authenticated) {
    await loadDashboard();
    await loadLibraryRecords();
    await loadPrivateSources();
  }
}

async function updateUserApiKey(value: string): Promise<void> {
  const nextValue = value.trim();
  persistUserApiKey(nextValue);
  state = {
    ...state,
    userApiKey: nextValue,
    status: nextValue
      ? "Using your browser-session API key for generation."
      : "Using workspace AI configuration.",
  };
  render();
  await refreshSessionStatus();
}

async function updateUserAiProvider(value: AiProvider): Promise<void> {
  persistUserAiProvider(value);
  state = {
    ...state,
    userAiProvider: value,
    status: `Using ${formatUserAiProvider(value)} for browser-session generation.`,
  };
  render();
  await refreshSessionStatus();
}

function updateResearchApiKeyField(field: string, value: string): void {
  const key = value.trim();
  switch (field) {
    case "embedding-api-key":
      updateResearchApiKeys({ embeddingApiKey: key });
      return;
    case "scholarai-api-key":
      updateResearchApiKeys({ scholarAiApiKey: key });
      return;
    case "regulations-api-key":
      updateResearchApiKeys({ regulationsApiKey: key });
      return;
    case "nvd-api-key":
      updateResearchApiKeys({ nvdApiKey: key });
      return;
    default:
      return;
  }
}

function updateResearchApiKeys(
  nextKeys: Partial<
    Pick<AppState, "embeddingApiKey" | "scholarAiApiKey" | "regulationsApiKey" | "nvdApiKey">
  >,
): void {
  const nextState = {
    embeddingApiKey: nextKeys.embeddingApiKey ?? state.embeddingApiKey,
    scholarAiApiKey: nextKeys.scholarAiApiKey ?? state.scholarAiApiKey,
    regulationsApiKey: nextKeys.regulationsApiKey ?? state.regulationsApiKey,
    nvdApiKey: nextKeys.nvdApiKey ?? state.nvdApiKey,
  };
  persistSessionSecret(EMBEDDING_API_KEY_STORAGE_KEY, nextState.embeddingApiKey);
  persistSessionSecret(SCHOLAR_AI_API_KEY_STORAGE_KEY, nextState.scholarAiApiKey);
  persistSessionSecret(REGULATIONS_API_KEY_STORAGE_KEY, nextState.regulationsApiKey);
  persistSessionSecret(NVD_API_KEY_STORAGE_KEY, nextState.nvdApiKey);
  state = {
    ...state,
    ...nextState,
    status: "Browser-session research keys updated.",
  };
  render();
}

async function deleteCurrentNote(noteId: string): Promise<void> {
  state = { ...state, status: "Deleting signal..." };
  render();

  try {
    await requestJson<{ ok: boolean }>(`/api/notes/${encodeURIComponent(noteId)}`, {
      method: "DELETE",
    });

    const remaining = state.notes.filter((note) => note.id !== noteId);
    state = {
      ...state,
      notes: remaining,
      selectedNoteId: remaining[0]?.id ?? null,
      status: "Signal deleted.",
    };
    render();
    await loadDashboard();
  } catch (error) {
    state = {
      ...state,
      status: error instanceof Error ? error.message : "Could not delete signal.",
    };
    render();
  }
}

function downloadWeeklyBrief(): void {
  const { brief, notes } = getCurrentBriefScope();
  const markdown = buildWeeklyBriefMarkdown(brief, notes);
  const filename = `${slugify(brief?.title || "pithy-notes-weekly-brief")}.md`;
  downloadTextFile(filename, markdown, "text/markdown;charset=utf-8");
  state = { ...state, status: "Weekly brief download started." };
  render();
}

function updateBriefFilterField(field: string, value: string): void {
  const nextFilters = { ...state.briefFilters };
  switch (field) {
    case "category":
      nextFilters.category = value;
      break;
    case "tag":
      nextFilters.tag = value;
      break;
    case "fromDate":
      nextFilters.fromDate = value;
      break;
    case "toDate":
      nextFilters.toDate = value;
      break;
    default:
      return;
  }

  state = {
    ...state,
    briefFilters: nextFilters,
    briefAiSummary: null,
    briefAiSummaryScopeKey: "",
  };
  render();
}

function updateOutputsFilterField(field: string, value: string): void {
  const nextFilters = { ...state.outputsFilters };
  switch (field) {
    case "status":
      nextFilters.status = value as OutputsFilterState["status"];
      break;
    case "platform":
      nextFilters.platform = value as OutputsFilterState["platform"];
      break;
    case "palette":
      nextFilters.palette = value as OutputsFilterState["palette"];
      break;
    case "sort":
      nextFilters.sort = value as OutputsFilterState["sort"];
      break;
    default:
      return;
  }

  state = {
    ...state,
    outputsFilters: nextFilters,
  };
  render();
}

async function saveCurrentLibraryRecord(): Promise<void> {
  if (
    !state.researchComposer.paperText.trim()
    && !state.researchBrief
    && !state.researchScript
    && !state.researchDeepDive
  ) {
    state = {
      ...state,
      researchMessage: "Create or paste some research content before saving to the library.",
    };
    render();
    return;
  }

  try {
    state = { ...state, researchMessage: "Saving to library..." };
    render();
    await persistResearchLibraryRecord(
      state,
      state.activeLibraryRecordId ? "Library record updated." : "Saved to library.",
    );
  } catch (error) {
    state = {
      ...state,
      researchMessage: error instanceof Error ? error.message : "Could not save to library.",
    };
    render();
  }
}

async function persistResearchLibraryRecord(
  snapshot: AppState,
  successMessage?: string,
): Promise<ResearchLibraryRecord> {
  const isAssessment = snapshot.researchComposer.workflow === "assessment";
  const sourceUrls = isAssessment ? [] : getResearchComposerSourceUrls(snapshot.researchComposer);
  const payload: SaveResearchLibraryRecordInput = {
    id: snapshot.activeLibraryRecordId ?? undefined,
    title:
      isAssessment
        ? snapshot.assessmentComposer.workExample.trim() || undefined
        : snapshot.researchComposer.title.trim() || undefined,
    workflow: snapshot.researchComposer.workflow,
    mode: snapshot.researchComposer.mode,
    sourceUrl: sourceUrls[0] ?? undefined,
    sourceUrls,
    paperText:
      isAssessment
        ? snapshot.assessmentComposer.workExample
        : snapshot.researchComposer.paperText,
    assessmentInput:
      isAssessment
        ? snapshot.assessmentComposer
        : undefined,
    assessmentReport: snapshot.assessmentReport,
    intelligenceBrief: snapshot.researchBrief,
    brief: snapshot.researchBrief,
    script: isAssessment ? null : snapshot.researchScript,
    deepDive: isAssessment ? null : snapshot.researchDeepDive,
  };
  const response = await requestJson<{ record: ResearchLibraryRecord }>("/api/library-records", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  state = {
    ...state,
    activeLibraryRecordId: response.record.id,
    libraryRecords: mergeLibraryRecords(response.record, state.libraryRecords),
    researchMessage: successMessage
      ? `${successMessage} Use Add New Signal to start another one.`
      : state.researchMessage,
  };
  render();
  return response.record;
}

function startNewResearchSignal(): void {
  resetResearchWorkspace("New signal workspace ready. Paste the next title, source, and research text.");
}

function clearResearchWorkspace(): void {
  resetResearchWorkspace("Workspace cleared. Saved library records are still available in Library.");
}

function resetResearchWorkspace(researchMessage: string): void {
  state = {
    ...state,
    activeLibraryRecordId: null,
    researchComposer: createEmptyResearchComposer(),
    assessmentComposer: createEmptyAssessmentComposer(),
    assessmentReport: null,
    researchBrief: null,
    researchScript: null,
    researchDeepDive: null,
    isGeneratingResearchBrief: false,
    isGeneratingResearchScript: false,
    isGeneratingResearchDeepDive: false,
    isGeneratingBriefingVideoRecordId: null,
    privateSourceResults: [],
    isSearchingPrivateSources: false,
    scholarAiResults: [],
    isSearchingScholarAi: false,
    privateSourceMessage: "Save documents once, then search and attach relevant excerpts to new briefs.",
    researchMessage,
  };
  render();
}

async function openLibraryRecord(recordId: string): Promise<void> {
  const existing = state.libraryRecords.find((record) => record.id === recordId);
  const record = existing
    ?? (await requestJson<{ record: ResearchLibraryRecord }>(`/api/library-records/${encodeURIComponent(recordId)}`))
      .record;

  state = {
    ...state,
    activeView: "enrichment",
    activeLibraryRecordId: record.id,
    researchComposer: {
      workflow: "intelligence",
      mode: record.mode,
      title: record.title,
      sourceUrl: "",
      sourceUrls: normalizeResearchSourceUrls(record.sourceUrls ?? [record.sourceUrl ?? ""]),
      paperText: record.paperText,
      documents: [],
      privateSourceQuery: "",
      scholarQuery: "",
    },
    assessmentComposer: record.assessmentInput ?? createEmptyAssessmentComposer(),
    assessmentReport: null,
    researchBrief: record.intelligenceBrief ?? record.brief ?? null,
    researchScript: (record.workflow ?? "intelligence") === "assessment" ? null : record.script,
    researchDeepDive: (record.workflow ?? "intelligence") === "assessment" ? null : record.deepDive,
    researchMessage: "Library record opened.",
    isProfileMenuOpen: false,
  };
  if (window.location.hash.replace(/^#/, "") !== "enrichment") {
    window.location.hash = "enrichment";
  }
  render();
}

async function deleteLibraryRecordById(recordId: string): Promise<void> {
  try {
    await requestJson<{ ok: boolean }>(`/api/library-records/${encodeURIComponent(recordId)}`, {
      method: "DELETE",
    });
    state = {
      ...state,
      libraryRecords: state.libraryRecords.filter((record) => record.id !== recordId),
    };
    render();
  } catch {
    state = {
      ...state,
      researchMessage: "Could not delete library record.",
    };
    render();
  }
}

async function copyReferenceToClipboard(reference: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(reference);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = reference;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    textArea.remove();
  } catch {
    // Browsers can block clipboard access; the reference text remains visible.
  }
}

async function loadBriefAiSummary(force = false): Promise<void> {
  const { brief, notes, scopeKey } = getCurrentBriefScope();
  if (!brief || state.isLoadingBriefAiSummary) {
    return;
  }

  if (!force && state.briefAiSummary && state.briefAiSummaryScopeKey === scopeKey) {
    return;
  }

  state = { ...state, isLoadingBriefAiSummary: true };
  render();

  try {
    const response = await requestJson<{ summary: WeeklyBriefAiSummary }>("/api/brief-summary", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        brief,
        notes,
      }),
    });

    state = {
      ...state,
      briefAiSummary: response.summary,
      isLoadingBriefAiSummary: false,
      briefAiSummaryScopeKey: scopeKey,
    };
    render();
  } catch {
    state = {
      ...state,
      isLoadingBriefAiSummary: false,
    };
    render();
  }
}

async function uploadResearchDocuments(fileList: FileList | null): Promise<void> {
  const files = Array.from(fileList ?? []);
  if (!files.length) {
    return;
  }

  const documents: UploadedResearchDocument[] = [];
  const skipped: string[] = [];

  for (const file of files) {
    if (!isSupportedResearchDocument(file)) {
      skipped.push(file.name);
      continue;
    }

    try {
      const text = await file.text();
      documents.push({
        id: `doc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        size: file.size,
        type: file.type || inferDocumentType(file.name),
        text,
        addedAt: new Date().toISOString(),
      });
    } catch {
      skipped.push(file.name);
    }
  }

  if (!documents.length) {
    state = {
      ...state,
      researchMessage:
        skipped.length
          ? `Could not import ${skipped.join(", ")}. Use text, Markdown, CSV, JSON, HTML, XML, or log files for now.`
          : "No supported documents were selected.",
    };
    render();
    return;
  }

  const documentText = documents.map(formatUploadedDocumentForResearchText).join("\n\n");
  const paperText = [state.researchComposer.paperText.trim(), documentText]
    .filter(Boolean)
    .join("\n\n");
  const suffix = skipped.length
    ? ` ${skipped.length} file${skipped.length === 1 ? "" : "s"} skipped.`
    : "";

  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      paperText,
      documents: [...state.researchComposer.documents, ...documents],
    },
    researchMessage: `${documents.length} document${documents.length === 1 ? "" : "s"} added to Research Text.${suffix}`,
  };
  render();
}

async function saveResearchDocumentsToPrivateSources(): Promise<void> {
  const documents = state.researchComposer.documents;
  if (!documents.length) {
    state = {
      ...state,
      privateSourceMessage: "Upload one or more documents before saving to Private Source Library.",
    };
    render();
    return;
  }

  state = { ...state, privateSourceMessage: "Saving documents to Private Source Library..." };
  render();
  try {
    const saved: PrivateSourceRecord[] = [];
    for (const document of documents) {
      const response = await requestJson<{ source: PrivateSourceRecord }>("/api/private-sources", {
        method: "POST",
        body: JSON.stringify({
          title: document.name.replace(/\.[^.]+$/, ""),
          filename: document.name,
          mimeType: document.type,
          size: document.size,
          text: document.text,
        }),
      });
      saved.push(response.source);
    }

    state = {
      ...state,
      privateSources: mergePrivateSources(saved, state.privateSources),
      privateSourceMessage: `${saved.length} document${saved.length === 1 ? "" : "s"} saved to Private Source Library.`,
    };
    render();
  } catch (error) {
    state = {
      ...state,
      privateSourceMessage:
        error instanceof Error ? error.message : "Could not save private source documents.",
    };
    render();
  }
}

async function searchPrivateSourcesForComposer(): Promise<void> {
  const query = state.researchComposer.privateSourceQuery.trim();
  if (!query) {
    state = {
      ...state,
      privateSourceResults: [],
      privateSourceMessage: "Enter a search term to retrieve private source excerpts.",
    };
    render();
    return;
  }

  state = {
    ...state,
    isSearchingPrivateSources: true,
    privateSourceMessage: "Searching private sources...",
  };
  render();
  try {
    const response = await requestJson<{ results: PrivateSourceSearchResult[] }>(
      "/api/private-sources/search",
      {
        method: "POST",
        body: JSON.stringify({ query, limit: 8 }),
      },
    );
    state = {
      ...state,
      privateSourceResults: response.results,
      isSearchingPrivateSources: false,
      privateSourceMessage: response.results.length
        ? `${response.results.length} excerpt${response.results.length === 1 ? "" : "s"} found.`
        : "No private source excerpts matched that search.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      isSearchingPrivateSources: false,
      privateSourceMessage:
        error instanceof Error ? error.message : "Could not search private sources.",
    };
    render();
  }
}

function attachPrivateSourceResult(resultIndex: number): void {
  const result = state.privateSourceResults[resultIndex];
  if (!result) {
    return;
  }

  const block = [
    `--- Private Source: ${result.title} ---`,
    result.filename ? `File: ${result.filename}` : "",
    result.sourceUrl ? `URL: ${result.sourceUrl}` : "",
    `Excerpt ${result.chunkIndex + 1}:`,
    result.chunkText.trim(),
    `--- End Private Source: ${result.title} ---`,
  ]
    .filter(Boolean)
    .join("\n");
  const paperText = [state.researchComposer.paperText.trim(), block].filter(Boolean).join("\n\n");

  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      paperText,
    },
    privateSourceMessage: `Attached excerpt from ${result.title}.`,
    researchMessage: "Private source excerpt added to Research Text.",
  };
  render();
}

async function searchScholarAiForComposer(): Promise<void> {
  const query =
    state.researchComposer.scholarQuery.trim()
    || state.researchComposer.title.trim()
    || state.researchComposer.paperText.trim().slice(0, 240);
  if (!query) {
    state = {
      ...state,
      scholarAiResults: [],
      scholarAiMessage: "Enter a research question, title, or topic before searching ScholarAI.",
    };
    render();
    return;
  }

  state = {
    ...state,
    isSearchingScholarAi: true,
    scholarAiMessage: "Searching ScholarAI for authoritative papers...",
  };
  render();
  try {
    const response = await requestJson<{ results: ScholarAiPaperResult[] }>(
      "/api/research/scholarai-search",
      {
        method: "POST",
        body: JSON.stringify({ query, limit: 6 }),
      },
    );
    state = {
      ...state,
      scholarAiResults: response.results,
      isSearchingScholarAi: false,
      scholarAiMessage: response.results.length
        ? `${response.results.length} paper${response.results.length === 1 ? "" : "s"} found. Attach the ones you want to use.`
        : "ScholarAI did not return papers for that search.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      isSearchingScholarAi: false,
      scholarAiMessage: error instanceof Error ? error.message : "Could not search ScholarAI.",
    };
    render();
  }
}

function attachScholarAiResult(resultIndex: number): void {
  const paper = state.scholarAiResults[resultIndex];
  if (!paper) {
    return;
  }

  const sourceUrl = paper.url || (paper.doi ? `https://doi.org/${paper.doi}` : "");
  const block = [
    `--- ScholarAI Paper: ${paper.title} ---`,
    paper.authors.length ? `Authors: ${paper.authors.join(", ")}` : "",
    paper.publicationDate ? `Published: ${paper.publicationDate}` : "",
    typeof paper.citedByCount === "number" ? `Cited by: ${paper.citedByCount}` : "",
    paper.doi ? `DOI: ${paper.doi}` : "",
    sourceUrl ? `URL: ${sourceUrl}` : "",
    paper.answer ? `ScholarAI answer:\n${paper.answer}` : "",
    paper.abstract ? `Abstract:\n${paper.abstract}` : "",
    `--- End ScholarAI Paper: ${paper.title} ---`,
  ]
    .filter(Boolean)
    .join("\n");
  const paperText = [state.researchComposer.paperText.trim(), block].filter(Boolean).join("\n\n");
  const sourceUrls = sourceUrl
    ? normalizeResearchSourceUrls([...state.researchComposer.sourceUrls, sourceUrl])
    : state.researchComposer.sourceUrls;

  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      paperText,
      sourceUrls,
    },
    scholarAiMessage: `Attached "${paper.title}" to Research Text.`,
    researchMessage: "ScholarAI paper added to this record.",
  };
  render();
}

function removeResearchDocument(documentId: string): void {
  const document = state.researchComposer.documents.find((item) => item.id === documentId);
  if (!document) {
    return;
  }

  const documentBlock = formatUploadedDocumentForResearchText(document);
  const paperText = state.researchComposer.paperText
    .replace(documentBlock, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      paperText,
      documents: state.researchComposer.documents.filter((item) => item.id !== documentId),
    },
    researchMessage: `${document.name} removed from this record.`,
  };
  render();
}

function updateResearchComposerField(
  field: string,
  value: string,
  options: { renderAfterUpdate?: boolean } = {},
): void {
  const composer = { ...state.researchComposer };
  switch (field) {
    case "workflow":
      composer.workflow = value === "assessment" ? "intelligence" : value as ReportWorkflow;
      state = {
        ...state,
        researchComposer: composer,
        assessmentReport: null,
        researchBrief: null,
        researchScript: null,
        researchDeepDive: null,
      };
      break;
    case "mode":
      composer.mode = value as ResearchBriefMode;
      break;
    case "title":
      composer.title = value;
      break;
    case "sourceUrl":
      composer.sourceUrl = value;
      break;
    case "paperText":
      composer.paperText = value;
      break;
    case "privateSourceQuery":
      composer.privateSourceQuery = value;
      break;
    case "scholarQuery":
      composer.scholarQuery = value;
      break;
    default:
      return;
  }

  state = {
    ...state,
    researchComposer: composer,
    researchMessage:
      composer.workflow === "assessment"
        ? "Capture your reflection or example, then create an assessment report."
        : "Paste research text and create an intelligence brief, then a script.",
  };
  if (options.renderAfterUpdate ?? true) {
    render();
  }
}

function updateAssessmentComposerField(field: string, value: string): void {
  state = {
    ...state,
    assessmentComposer: {
      ...state.assessmentComposer,
      [field]: value,
    },
    researchMessage:
      "Capture your reflection or example, then create an assessment report.",
  };
  render();
}

function addResearchSource(): void {
  const candidate = state.researchComposer.sourceUrl.trim();
  if (!candidate) {
    return;
  }

  const sourceUrls = normalizeResearchSourceUrls([
    ...state.researchComposer.sourceUrls,
    candidate,
  ]);

  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      sourceUrl: "",
      sourceUrls,
    },
    researchMessage: "Source added to this record.",
  };
  render();
}

function clearResearchSourceInput(): void {
  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      sourceUrl: "",
    },
    researchMessage: "Source field cleared.",
  };
  render();
}

function removeResearchSource(sourceIndex: number): void {
  const sourceUrls = state.researchComposer.sourceUrls.filter((_, index) => index !== sourceIndex);
  state = {
    ...state,
    researchComposer: {
      ...state.researchComposer,
      sourceUrls,
    },
    researchMessage: state.activeLibraryRecordId
      ? "Source removed. Update the library record to save this change."
      : "Source removed.",
  };
  render();
}

async function generateResearchBrief(): Promise<void> {
  if (state.researchComposer.workflow === "assessment") {
    state = {
      ...state,
      researchComposer: {
        ...state.researchComposer,
        workflow: "intelligence",
      },
      assessmentReport: null,
    };
  }

  const composer = state.researchComposer;
  if (composer.workflow === "assessment") {
    const assessment = state.assessmentComposer;
    if (!assessment.workExample.trim()) {
      state = {
        ...state,
        researchMessage: "Add your own non-confidential reflection or example before creating the Assessment Report.",
      };
      render();
      return;
    }

    state = {
      ...state,
      isGeneratingResearchBrief: true,
      researchMessage: "Creating Assessment Report from your reflection or example...",
      assessmentReport: null,
      researchBrief: null,
      researchScript: null,
      researchDeepDive: null,
    };
    render();

    try {
      const response = await requestJson<{ report: PithySignalAssessmentReport }>(
        "/api/assessment/report",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(assessment),
        },
      );

      state = {
        ...state,
        assessmentReport: response.report,
        isGeneratingResearchBrief: false,
        researchMessage: "Assessment Report created.",
      };
      render();
      await persistResearchLibraryRecord(
        state,
        "Assessment Report created and saved to Library.",
      );
    } catch (error) {
      state = {
        ...state,
        isGeneratingResearchBrief: false,
        researchMessage:
          error instanceof Error ? error.message : "Could not generate Assessment Report.",
      };
      render();
    }
    return;
  }

  const briefLabel = getResearchBriefLabel(composer.mode);
  const sourceUrls = getResearchComposerSourceUrls(composer);
  if (!composer.paperText.trim()) {
    state = {
      ...state,
      researchMessage: `Paste research text before creating the ${briefLabel}.`,
    };
    render();
    return;
  }

  state = {
    ...state,
    isGeneratingResearchBrief: true,
    researchMessage: `Creating ${briefLabel} from your research text...`,
    researchScript: null,
    researchDeepDive: null,
  };
  render();

  try {
    const response = await requestJson<{ brief: GovernanceResearchBrief }>("/api/research/brief", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: composer.title,
        sourceUrl: sourceUrls[0],
        sourceUrls,
        paperText: composer.paperText,
        mode: composer.mode,
      }),
    });

    state = {
      ...state,
      researchBrief: response.brief,
      isGeneratingResearchBrief: false,
      researchMessage: `${briefLabel} created.`,
    };
    render();
    await persistResearchLibraryRecord(
      state,
      `${briefLabel} created and saved to Library.`,
    );
  } catch (error) {
    state = {
      ...state,
      isGeneratingResearchBrief: false,
      researchMessage:
        error instanceof Error ? error.message : `Could not generate ${briefLabel}.`,
    };
    render();
  }
}

async function generateResearchScript(): Promise<void> {
  if (state.researchComposer.workflow === "assessment") {
    state = {
      ...state,
      researchMessage: "Scripts are available only for intelligence briefs.",
    };
    render();
    return;
  }

  const brief = state.researchBrief;
  const briefLabel = getResearchBriefLabel(state.researchComposer.mode);
  if (!brief) {
    state = { ...state, researchMessage: `Generate the ${briefLabel} first.` };
    render();
    return;
  }

  state = {
    ...state,
    isGeneratingResearchScript: true,
    researchMessage: "Creating script...",
  };
  render();

  try {
    const sourceUrls = getResearchComposerSourceUrls(state.researchComposer);
    const response = await requestJson<{ script: AvatarScript }>("/api/research/script", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        input: {
          title: state.researchComposer.title,
          sourceUrl: sourceUrls[0],
          sourceUrls,
          paperText: state.researchComposer.paperText,
          mode: state.researchComposer.mode,
        },
        brief,
      }),
    });

    state = {
      ...state,
      researchScript: response.script,
      isGeneratingResearchScript: false,
      researchMessage: "Script ready.",
    };
    render();
    await persistResearchLibraryRecord(state, "Script ready. Saved to library.");
  } catch (error) {
    state = {
      ...state,
      isGeneratingResearchScript: false,
      researchMessage: error instanceof Error ? error.message : "Could not generate avatar script.",
    };
    render();
  }
}

async function generateResearchDeepDive(): Promise<void> {
  if (state.researchComposer.workflow === "assessment") {
    state = {
      ...state,
      researchMessage: "Deep dives are available only for intelligence briefs.",
    };
    render();
    return;
  }

  const brief = state.researchBrief;
  const briefLabel = getResearchBriefLabel(state.researchComposer.mode);
  if (!brief) {
    state = { ...state, researchMessage: `Generate the ${briefLabel} first.` };
    render();
    return;
  }

  state = {
    ...state,
    isGeneratingResearchDeepDive: true,
    researchMessage:
      "Generating deep-dive text on the highest-quality model path. This may take a while.",
  };
  render();

  try {
    const sourceUrls = getResearchComposerSourceUrls(state.researchComposer);
    const response = await requestJson<{ deepDive: DeepDiveScript }>("/api/research/deep-dive", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        input: {
          title: state.researchComposer.title,
          sourceUrl: sourceUrls[0],
          sourceUrls,
          paperText: state.researchComposer.paperText,
          mode: state.researchComposer.mode,
        },
        brief,
      }),
    });

    state = {
      ...state,
      researchDeepDive: response.deepDive,
      isGeneratingResearchDeepDive: false,
      researchMessage: "Deep-dive text ready.",
    };
    render();
    await persistResearchLibraryRecord(state, "Deep-dive text ready. Saved to library.");
  } catch (error) {
    state = {
      ...state,
      isGeneratingResearchDeepDive: false,
      researchMessage:
        error instanceof Error ? error.message : "Could not generate deep-dive text.",
    };
    render();
  }
}

function downloadResearchDeepDive(): void {
  const deepDive = state.researchDeepDive;
  if (!deepDive) {
    state = { ...state, researchMessage: "Generate a deep-dive first." };
    render();
    return;
  }

  const filename = `${slugify(deepDive.title || "pithy-notes-deep-dive")}.txt`;
  downloadTextFile(filename, deepDive.fullText, "text/plain;charset=utf-8");
  state = { ...state, researchMessage: "Deep-dive text download started." };
  render();
}

function downloadCurrentResearchRecord(): void {
  const record = buildCurrentResearchRecordSnapshot();
  if (
    !record.paperText.trim()
    && !record.assessmentReport
    && !record.intelligenceBrief
    && !record.brief
    && !record.script
    && !record.deepDive
  ) {
    state = {
      ...state,
      researchMessage:
        state.researchComposer.workflow === "assessment"
          ? "Create an assessment report before downloading a local copy."
          : "Create or paste some research content before downloading a local copy.",
    };
    render();
    return;
  }

  downloadResearchRecord(record);
  state = { ...state, researchMessage: "Local copy download started." };
  render();
}

function downloadLibraryRecord(recordId: string): void {
  const record = state.libraryRecords.find((item) => item.id === recordId);
  if (!record) {
    state = { ...state, researchMessage: "Could not find that library record to download." };
    render();
    return;
  }

  downloadResearchRecord(record);
  state = { ...state, researchMessage: "Library record download started." };
  render();
}

function downloadResearchRecord(record: ResearchLibraryRecord): void {
  const markdown = buildResearchRecordMarkdown(record);
  const filename = `${slugify(record.title || "pithy-signal-record")}.md`;
  downloadTextFile(filename, markdown, "text/markdown;charset=utf-8");
}

function updateCaptureComposerField(field: string, value: string): void {
  const composer = { ...state.captureComposer };
  switch (field) {
    case "title":
      composer.title = value;
      break;
    case "sourceUrl":
      composer.sourceUrl = value;
      break;
    case "sourceKind":
      composer.sourceKind = value as PithyNote["source"]["kind"];
      break;
    case "capturedText":
      composer.capturedText = value;
      break;
    case "category":
      composer.category = value;
      break;
    case "summary":
      composer.summary = value;
      break;
    case "signalStrength":
      composer.signalStrength = value as PithyNote["signalStrength"];
      break;
    case "riskType":
      composer.riskType = value as PithyNote["riskType"];
      break;
    case "suggestedAction":
      composer.suggestedAction = value as PithyNote["suggestedAction"];
      break;
    case "tags":
      composer.tags = value;
      break;
    default:
      return;
  }
  state = {
    ...state,
    captureComposer: composer,
    captureMessage: "",
  };
}

function updateCreateComposerField(field: string, value: string): void {
  const composer = { ...state.createComposer };
  switch (field) {
    case "rawThought":
      composer.rawThought = value;
      composer.sourceMode = "paste";
      break;
    case "platform":
      composer.platform = value as CapturePlatformMode;
      if (composer.platform === "linkedin" && composer.palette === "punch") {
        composer.palette = "signal";
      }
      if (composer.platform === "instagram" && composer.palette === "editorial") {
        composer.palette = "punch";
      }
      break;
    case "palette":
      composer.palette = value as PithyPaletteId;
      break;
    default:
      return;
  }

  state = {
    ...state,
    createComposer: composer,
    createPublishReceipt: createEmptyPublishReceipt(),
    createOutput: null,
    createHistory: composer.sourceMode === "paste" ? [] : state.createHistory,
    isLoadingCreateHistory: false,
    createMessage: "",
  };
  render();
}

function updateCreatePublishReceiptField(
  field: keyof CreatePublishReceiptState,
  value: string,
): void {
  state = {
    ...state,
    createPublishReceipt: {
      ...state.createPublishReceipt,
      [field]: value,
    },
    createMessage: "",
  };
  render();
}

async function createSignalCardsFromResearch(): Promise<void> {
  if (state.researchComposer.workflow === "assessment") {
    state = {
      ...state,
      researchMessage: "Signal Cards are available for research and intelligence briefs.",
    };
    render();
    return;
  }

  const rawThought = buildResearchSignalCardSource();
  if (!rawThought.trim()) {
    state = {
      ...state,
      researchMessage: "Create or paste source material before making Signal Cards.",
    };
    render();
    return;
  }

  state = {
    ...state,
    activeView: "create",
    isProfileMenuOpen: false,
    isBriefOpen: false,
    createComposer: {
      sourceMode: "paste",
      sourceNoteId: "",
      rawThought,
      platform: "linkedin",
      palette: "signal",
    },
    createOutput: null,
    createHistory: [],
    isLoadingCreateHistory: false,
    createPublishReceipt: createEmptyPublishReceipt(),
    createMessage: "Research signal loaded. Generating Signal Cards...",
  };

  if (window.location.hash.replace(/^#/, "") !== "create") {
    window.history.pushState(null, "", "#create");
  }

  render();
  await generateCreateOutput();
}

function buildResearchSignalCardSource(): string {
  const composer = state.researchComposer;
  const title = composer.title.trim();
  const sources = getResearchComposerSourceUrls(composer);
  const parts: string[] = [];

  if (title) {
    parts.push(`# ${title}`);
  }

  if (sources.length) {
    parts.push(["## Sources", ...sources.map((sourceUrl) => `- ${sourceUrl}`)].join("\n"));
  }

  if (state.researchBrief) {
    parts.push(["## Intelligence Brief", ...formatResearchBriefMarkdown(state.researchBrief)].join("\n"));
  }

  if (state.researchScript) {
    parts.push(
      [
        "## Script Notes",
        `Hook: ${state.researchScript.hook}`,
        "",
        "Sections:",
        ...state.researchScript.sections.map((section) => `- ${section}`),
        "",
        `Close: ${state.researchScript.close}`,
      ].join("\n"),
    );
  }

  if (state.researchDeepDive) {
    parts.push(
      [
        "## Deep Dive Notes",
        state.researchDeepDive.executiveFraming,
        "",
        "Practical takeaways:",
        ...state.researchDeepDive.practicalTakeaways.map((takeaway) => `- ${takeaway}`),
        "",
        state.researchDeepDive.closingPerspective,
      ].join("\n"),
    );
  }

  if (!state.researchBrief && composer.paperText.trim()) {
    parts.push(["## Source Material", composer.paperText.trim()].join("\n"));
  }

  return parts.join("\n\n").trim();
}

async function generateCreateOutput(refinementMode?: CaptureRefinementMode): Promise<void> {
  const composer = state.createComposer;
  const sourceNote =
    composer.sourceMode === "existing-note"
      ? state.notes.find((note) => note.id === composer.sourceNoteId) ?? null
      : null;
  const sourceReady = composer.sourceMode === "existing-note"
    ? Boolean(sourceNote)
    : Boolean(composer.rawThought.trim());

  if (!sourceReady) {
    state = {
      ...state,
      createMessage: "Choose a source note or paste a rough thought first.",
    };
    render();
    return;
  }

  const payload: CaptureOutputInput = {
    sourceNoteId: sourceNote?.id,
    sourceTitle:
      sourceNote?.title
      || summarizeCaptureText(composer.rawThought)
      || "Rough thought",
    sourceSummary: sourceNote?.summary || summarizeCaptureText(composer.rawThought),
    rawThought: composer.sourceMode === "paste" ? composer.rawThought.trim() : sourceNote?.selectedText,
    platform: composer.platform,
    palette: composer.palette,
    refinementMode,
    provenance: buildCreateOutputProvenance(sourceNote, composer.rawThought),
    currentOutput: state.createOutput
      ? {
          title: state.createOutput.title,
          slides: state.createOutput.slides,
          caption: state.createOutput.caption,
        }
      : undefined,
  };

  state = {
    ...state,
      isGeneratingCreate: true,
      createMessage: refinementMode
        ? `Refining Signal Cards: ${humanizeRefinementMode(refinementMode)}...`
        : composer.platform === "none"
          ? "Generating Signal Cards..."
          : `Generating ${formatCapturePlatformLabel(composer.platform)} Signal Cards...`,
  };
  render();

  try {
    const response = await requestJson<{ output: CaptureOutput }>("/api/capture-outputs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    state = {
      ...state,
      createOutput: response.output,
      createPublishReceipt: toCreatePublishReceiptState(response.output.publishReceipt),
      createHistory: mergeCreateHistory(response.output, state.createHistory),
      outputsLibrary: mergeCreateHistory(response.output, state.outputsLibrary),
      isGeneratingCreate: false,
      createMessage: refinementMode
        ? `Refinement applied: ${humanizeRefinementMode(refinementMode)}.`
        : "Signal Cards ready. Review the cards and caption.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      isGeneratingCreate: false,
      createMessage: error instanceof Error ? error.message : "Could not generate Signal Cards.",
    };
    render();
  }
}

async function loadCreateHistory(sourceNoteId: string): Promise<void> {
  if (!sourceNoteId) {
    state = {
      ...state,
      createHistory: [],
      isLoadingCreateHistory: false,
    };
    render();
    return;
  }

  state = {
    ...state,
    isLoadingCreateHistory: true,
  };
  render();

  try {
    const response = await requestJson<{ outputs: CaptureOutput[] }>(
      `/api/capture-outputs?sourceNoteId=${encodeURIComponent(sourceNoteId)}`,
    );
    state = {
      ...state,
      createHistory: response.outputs,
      isLoadingCreateHistory: false,
    };
    render();
  } catch {
    state = {
      ...state,
      createHistory: [],
      isLoadingCreateHistory: false,
    };
    render();
  }
}

function loadCreateOutputFromHistory(outputId: string): void {
  const output = state.createHistory.find((item) => item.id === outputId);
  if (!output) {
    state = {
      ...state,
      createMessage: "That saved variant is no longer available.",
    };
    render();
    return;
  }

  state = {
    ...state,
    createOutput: output,
    createPublishReceipt: toCreatePublishReceiptState(output.publishReceipt),
    createComposer: {
      ...state.createComposer,
      platform: output.platform,
      palette: output.palette,
    },
    createMessage: "Saved variant loaded.",
  };
  render();
}

function openOutputLibraryItem(outputId: string): void {
  const output = state.outputsLibrary.find((item) => item.id === outputId);
  if (!output) {
    state = {
      ...state,
      status: "That output is no longer available.",
    };
    render();
    return;
  }

  state = {
    ...state,
    activeView: "create",
    createOutput: output,
    createPublishReceipt: toCreatePublishReceiptState(output.publishReceipt),
    createComposer: {
      ...state.createComposer,
      sourceMode: output.sourceNoteId ? "existing-note" : "paste",
      sourceNoteId: output.sourceNoteId ?? "",
      rawThought: output.sourceNoteId ? state.createComposer.rawThought : output.sourceSummary,
      platform: output.platform,
      palette: output.palette,
    },
    createHistory: output.sourceNoteId
      ? state.outputsLibrary.filter((item) => item.sourceNoteId === output.sourceNoteId)
      : [output],
    isLoadingCreateHistory: false,
    createMessage: "Output loaded from library.",
  };
  render();
}

async function branchCreateOutputFromHistory(outputId: string): Promise<void> {
  const output = state.createHistory.find((item) => item.id === outputId);
  if (!output) {
    state = {
      ...state,
      createMessage: "That saved variant is no longer available.",
    };
    render();
    return;
  }

  state = {
    ...state,
    createOutput: output,
    createPublishReceipt: toCreatePublishReceiptState(output.publishReceipt),
    createComposer: {
      ...state.createComposer,
      platform: output.platform,
      palette: output.palette,
    },
    createMessage: "Branching from saved variant...",
  };
  render();

  await generateCreateOutput();
}

async function updateCreateOutputStatus(status: CaptureOutputStatus): Promise<void> {
  const output = state.createOutput;
  if (!output) {
    state = {
      ...state,
      createMessage: "Generate or open Signal Cards before changing their status.",
    };
    render();
    return;
  }

  state = {
    ...state,
    createMessage: `Updating status to ${titleizeCaptureStatus(status).toLowerCase()}...`,
  };
  render();

  try {
    const response = await requestJson<{ output: CaptureOutput }>(
      `/api/capture-outputs/${encodeURIComponent(output.id)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status,
          provenance: resolveOutputProvenance(output),
        }),
      },
    );

    state = {
      ...state,
      createOutput: response.output,
      createHistory: mergeCreateHistory(response.output, state.createHistory),
      outputsLibrary: mergeCreateHistory(response.output, state.outputsLibrary),
      createMessage: `Status updated: ${titleizeCaptureStatus(status)}.`,
    };
    render();
  } catch (error) {
    state = {
      ...state,
      createMessage:
        error instanceof Error ? error.message : "Could not update Signal Card status.",
    };
    render();
  }
}

async function deleteCreateOutput(outputId: string): Promise<void> {
  const isCurrent = state.createOutput?.id === outputId;
  state = {
    ...state,
    createMessage: "Deleting output...",
  };
  render();

  try {
    await requestJson<{ ok: boolean }>(`/api/capture-outputs/${encodeURIComponent(outputId)}`, {
      method: "DELETE",
    });

    const nextHistory = state.createHistory.filter((item) => item.id !== outputId);
    const nextLibrary = state.outputsLibrary.filter((item) => item.id !== outputId);
    state = {
      ...state,
      createOutput: isCurrent ? null : state.createOutput,
      createPublishReceipt: isCurrent ? createEmptyPublishReceipt() : state.createPublishReceipt,
      createHistory: nextHistory,
      outputsLibrary: nextLibrary,
      createMessage: "Output deleted.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      createMessage: error instanceof Error ? error.message : "Could not delete output.",
    };
    render();
  }
}

async function duplicateCreateOutput(outputId: string): Promise<void> {
  state = {
    ...state,
    createMessage: "Duplicating output...",
  };
  render();

  try {
    const response = await requestJson<{ output: CaptureOutput }>("/api/capture-outputs/duplicate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outputId }),
    });

    const nextHistory =
      response.output.sourceNoteId &&
      state.createComposer.sourceMode === "existing-note" &&
      state.createComposer.sourceNoteId === response.output.sourceNoteId
        ? mergeCreateHistory(response.output, state.createHistory)
        : state.createHistory;

    state = {
      ...state,
      createOutput: response.output,
      createPublishReceipt: toCreatePublishReceiptState(response.output.publishReceipt),
      createHistory: nextHistory,
      outputsLibrary: mergeCreateHistory(response.output, state.outputsLibrary),
      createMessage: "Output duplicated.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      createMessage: error instanceof Error ? error.message : "Could not duplicate output.",
    };
    render();
  }
}

async function exportCreatePngSet(): Promise<void> {
  const output = state.createOutput;
  if (!output) {
    state = { ...state, createMessage: "Generate Signal Cards before exporting them." };
    render();
    return;
  }

  state = { ...state, createMessage: "Exporting PNG set..." };
  render();

  try {
    for (const [index, slide] of output.slides.entries()) {
      const svg = buildCreateExportSvg(output, slide, index);
      const blob = await renderSvgToPngBlob(svg, 1080, 1350);
      downloadBlob(
        `${slugify(output.title || output.sourceTitle)}-slide-${index + 1}.png`,
        blob,
      );
    }

    state = { ...state, createMessage: "PNG set export started." };
    render();
  } catch (error) {
    state = {
      ...state,
      createMessage: error instanceof Error ? error.message : "Could not export PNG set.",
    };
    render();
  }
}

function exportCreatePdf(): void {
  const output = state.createOutput;
  if (!output) {
    state = { ...state, createMessage: "Generate Signal Cards before exporting them." };
    render();
    return;
  }

  const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1200,height=900");
  if (!printWindow) {
    state = { ...state, createMessage: "Allow pop-ups to save the Signal Cards as PDF." };
    render();
    return;
  }

  const palette = getCreatePaletteTheme(output.palette);
  const cards = output.slides
    .map(
      (slide, index) => `
        <section class="page">
          <div class="card">
            <div class="meta">
              <span>Slide ${index + 1}</span>
              <span>${escapeHtml(titleizeCaptureSlideLabel(slide.role))}</span>
            </div>
            <h1>${escapeHtml(slide.title)}</h1>
            <p>${escapeHtml(slide.body).replaceAll("\n", "<br />")}</p>
            <footer>${escapeHtml(buildCreateFooterLabel(output.platform))}</footer>
          </div>
        </section>
      `,
    )
    .join("");

  printWindow.document.write(`
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(output.title)}</title>
        <style>
          @page { size: 1080px 1350px; margin: 0; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; background: #f5efe7; font-family: "Avenir Next", "Segoe UI", sans-serif; }
          .page { width: 1080px; height: 1350px; page-break-after: always; display: flex; align-items: stretch; justify-content: stretch; }
          .page:last-child { page-break-after: auto; }
          .card {
            width: 100%;
            height: 100%;
            padding: 96px 88px;
            display: flex;
            flex-direction: column;
            background: ${palette.background};
            color: ${palette.foreground};
          }
          .meta {
            display: flex;
            justify-content: space-between;
            font-size: 28px;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: ${palette.muted};
          }
          h1 {
            margin: 110px 0 36px;
            font-size: 88px;
            line-height: 1.03;
            letter-spacing: -0.05em;
          }
          p {
            margin: 0;
            font-size: 42px;
            line-height: 1.45;
            max-width: 18ch;
            white-space: pre-wrap;
          }
          footer {
            margin-top: auto;
            padding-top: 48px;
            font-size: 26px;
            color: ${palette.muted};
          }
        </style>
      </head>
      <body>${cards}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();

  state = { ...state, createMessage: "Print dialog opened. Save it as PDF from your browser." };
  render();
}

function exportCreateCaption(): void {
  const output = state.createOutput;
  if (!output) {
    state = { ...state, createMessage: "Generate Signal Cards before exporting them." };
    render();
    return;
  }

  downloadTextFile(
    `${slugify(output.title || output.sourceTitle)}-caption.txt`,
    output.caption,
    "text/plain;charset=utf-8",
  );
  state = { ...state, createMessage: "Caption export started." };
  render();
}

async function draftCaptureInApp(): Promise<void> {
  const composer = state.captureComposer;
  if (!composer.capturedText.trim() && !composer.title.trim()) {
    state = { ...state, captureMessage: "Add a title or paste captured text before drafting." };
    render();
    return;
  }

  state = { ...state, isDraftingCapture: true, captureMessage: "Drafting research brief..." };
  render();

  try {
    const draft = await requestJson<{
      draft: Pick<
        PithyNote,
        | "title"
        | "summary"
        | "signalStrength"
        | "riskType"
        | "category"
        | "suggestedAction"
        | "slide"
      > & { tags: string[] };
    }>("/api/notes/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: composer.title,
        sourceKind: composer.sourceKind,
        sourceLabel: composer.title || "Manual capture",
        sourceUrl: composer.sourceUrl,
        selectedText: composer.capturedText,
        category: composer.category,
      }),
    });

    state = {
      ...state,
      isDraftingCapture: false,
      captureComposer: {
        ...state.captureComposer,
        title: draft.draft.title || composer.title,
        summary: draft.draft.summary,
        signalStrength: draft.draft.signalStrength,
        riskType: draft.draft.riskType,
        category: draft.draft.category,
        suggestedAction: draft.draft.suggestedAction,
        tags: (draft.draft.tags ?? []).join(", "),
        slide: draft.draft.slide,
      },
      captureMessage: "Research brief applied.",
    };
    render();
  } catch (error) {
    state = {
      ...state,
      isDraftingCapture: false,
      captureMessage: error instanceof Error ? error.message : "Could not draft research brief.",
    };
    render();
  }
}

async function saveCaptureInApp(): Promise<void> {
  const composer = state.captureComposer;
  if (!composer.capturedText.trim() && !composer.summary.trim()) {
    state = { ...state, captureMessage: "Paste capture text or draft a summary before saving." };
    render();
    return;
  }

  const note = buildNoteFromComposer(composer);
  state = { ...state, isSavingCapture: true, captureMessage: "Saving signal to inbox..." };
  render();

  try {
    const response = await requestJson<{ note: PithyNote }>("/api/notes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(note),
    });

    state = {
      ...state,
      isSavingCapture: false,
      selectedNoteId: response.note.id,
      captureComposer: createEmptyCaptureComposer(),
      captureMessage: "Signal saved to inbox.",
      activeView: "capture",
    };
    await loadDashboard();
  } catch (error) {
    state = {
      ...state,
      isSavingCapture: false,
      captureMessage: error instanceof Error ? error.message : "Could not save signal.",
    };
    render();
  }
}

function buildNavItem(item: {
  id: SignalView;
  label: string;
  icon: string;
  badge?: () => number | null;
}): string {
  const isActive = state.activeView === item.id;
  const badgeValue = item.badge?.();
  const navAttributes =
    item.id === "profile"
      ? ' href="#profile" data-action="open-profile"'
      : ` href="#${escapeAttribute(item.id)}" data-nav="${escapeAttribute(item.id)}"`;
  return `
    <a class="nav-item ${isActive ? "is-active" : ""}"${navAttributes}>
      <span class="nav-icon">${buildSidebarIcon(item.icon)}</span>
      <span class="nav-label">${escapeHtml(item.label)}</span>
      ${badgeValue ? `<span class="nav-badge">${badgeValue}</span>` : ""}
    </a>
  `;
}

function buildCreateModeButton(mode: CreateComposerState["sourceMode"], label: string): string {
  const isActive = state.createComposer.sourceMode === mode;
  return `<button class="create-mode-button ${isActive ? "is-active" : ""}" data-action="set-create-mode" data-create-mode="${escapeAttribute(mode)}">${escapeHtml(label)}</button>`;
}

function buildContentReminder(): string {
  return `
    <section class="content-reminder" role="note">
      <strong>Content reminder</strong>
      <p>Only add content you have the right to use. Do not enter confidential, proprietary, regulated, or employer-sensitive information.</p>
    </section>
  `;
}

function buildCreateSourceCard(note: PithyNote, selected: boolean): string {
  return `
    <button class="create-source-card ${selected ? "is-selected" : ""}" data-create-source-note-id="${escapeAttribute(note.id)}">
      <div class="create-source-card-header">
        <strong>${escapeHtml(note.title)}</strong>
        <span class="generated-pill">${escapeHtml(formatLongDate(note.capturedAt))}</span>
      </div>
      <p>${escapeHtml(note.summary)}</p>
    </button>
  `;
}

function buildCaptureComposerMarkup(): string {
  const composer = state.captureComposer;

  return `
    <section class="capture-composer">
      <div class="capture-composer-header">
        <div>
          <h3>Capture inside Signal</h3>
          <p>Use your own notes, reflections, and non-confidential source material.</p>
        </div>
        <button class="composer-clear-button" data-action="clear-capture">Clear</button>
      </div>

      ${buildContentReminder()}

      <div class="capture-composer-grid">
        <label class="composer-field composer-field-wide">
          <span>Signal title</span>
          <input data-capture-field="title" value="${escapeAttribute(composer.title)}" placeholder="Global clean energy investment surges to record $2.1 trillion" />
        </label>

        <label class="composer-field">
          <span>Source URL</span>
          <input data-capture-field="sourceUrl" value="${escapeAttribute(composer.sourceUrl)}" placeholder="https://example.com/article" />
        </label>

        <label class="composer-field">
          <span>Source type</span>
          <select data-capture-field="sourceKind">
            ${buildSourceKindOptions(composer.sourceKind)}
          </select>
        </label>
      </div>

      <label class="composer-field">
        <span>Captured text</span>
        <textarea data-capture-field="capturedText" rows="7" placeholder="Paste your own notes, reflections, or non-confidential source material here.">${escapeHtml(composer.capturedText)}</textarea>
      </label>

      <div class="capture-composer-grid">
        <label class="composer-field">
          <span>Category</span>
          <input data-capture-field="category" value="${escapeAttribute(composer.category)}" placeholder="Energy transition" />
        </label>

        <label class="composer-field">
          <span>Tags</span>
          <input data-capture-field="tags" value="${escapeAttribute(composer.tags)}" placeholder="investment, energy, grid" />
        </label>
      </div>

      <label class="composer-field">
        <span>Summary</span>
        <textarea data-capture-field="summary" rows="3" placeholder="What matters, in one or two crisp lines.">${escapeHtml(composer.summary)}</textarea>
      </label>

      <div class="capture-composer-grid capture-composer-grid-tight">
        <label class="composer-field">
          <span>Signal strength</span>
          <select data-capture-field="signalStrength">
            ${buildSelectOptions(
              [
                ["low", "Low"],
                ["medium", "Medium"],
                ["high", "High"],
              ],
              composer.signalStrength,
            )}
          </select>
        </label>

        <label class="composer-field">
          <span>Risk type</span>
          <select data-capture-field="riskType">
            ${buildSelectOptions(
              [
                ["vendor", "Vendor"],
                ["regulatory", "Regulatory"],
                ["geopolitical", "Geopolitical"],
                ["operational", "Operational"],
                ["security", "Security"],
                ["reputational", "Reputational"],
              ],
              composer.riskType,
            )}
          </select>
        </label>

        <label class="composer-field">
          <span>Suggested action</span>
          <select data-capture-field="suggestedAction">
            ${buildSelectOptions(
              [
                ["monitor", "Monitor"],
                ["prepare", "Prepare"],
                ["act", "Act"],
              ],
              composer.suggestedAction,
            )}
          </select>
        </label>
      </div>

      <div class="capture-composer-actions">
        <p class="capture-composer-status">${escapeHtml(state.captureMessage)}</p>
        <div class="capture-composer-buttons">
          <button class="composer-secondary-button" data-action="draft-capture" ${state.isDraftingCapture ? "disabled" : ""}>
            ${state.isDraftingCapture ? "Drafting..." : "Enhance with AI"}
          </button>
          <button class="composer-primary-button" data-action="save-capture" ${state.isSavingCapture ? "disabled" : ""}>
            ${state.isSavingCapture ? "Saving..." : "Save to inbox"}
          </button>
        </div>
      </div>

      ${buildTrustPanel("capture")}
    </section>
  `;
}

function buildBriefModalMarkup(): string {
  const { brief, notes: scopedNotes } = getCurrentBriefScope();
  const themes = brief?.themes ?? [];
  const topSignals = brief?.topSignals ?? [];
  const watchlist = brief?.watchlist ?? [];
  const aiSummary = state.briefAiSummary;
  const categories = getBriefFilterCategories();
  const tags = getBriefFilterTags();
  const filters = state.briefFilters;

  return `
    <div class="brief-modal-backdrop" data-action="close-brief"></div>
    <section class="brief-modal" role="dialog" aria-modal="true" aria-label="Weekly brief">
      <header class="brief-modal-header">
        <div>
          <p class="brief-modal-eyebrow">Weekly brief</p>
          <h2>${escapeHtml(brief?.title ?? "Your weekly brief is not ready yet")}</h2>
          <p class="brief-modal-subtitle">${escapeHtml(brief?.dateLabel ? `Week of ${brief.dateLabel}` : "Capture signals to generate a brief.")}</p>
        </div>
        <button class="brief-modal-close" data-action="close-brief" aria-label="Close weekly brief">Close</button>
      </header>

      <div class="brief-modal-body">
        <section class="brief-filter-bar">
          <div class="brief-filter-grid">
            <label class="brief-filter-field">
              <span>Category</span>
              <select data-brief-filter="category">
                <option value="">All categories</option>
                ${categories.map((category) => `<option value="${escapeAttribute(category)}" ${filters.category === category ? "selected" : ""}>${escapeHtml(category)}</option>`).join("")}
              </select>
            </label>

            <label class="brief-filter-field">
              <span>Tag</span>
              <select data-brief-filter="tag">
                <option value="">All tags</option>
                ${tags.map((tag) => `<option value="${escapeAttribute(tag)}" ${filters.tag === tag ? "selected" : ""}>${escapeHtml(tag)}</option>`).join("")}
              </select>
            </label>

            <label class="brief-filter-field">
              <span>From</span>
              <input type="date" data-brief-filter="fromDate" value="${escapeAttribute(filters.fromDate)}" />
            </label>

            <label class="brief-filter-field">
              <span>To</span>
              <input type="date" data-brief-filter="toDate" value="${escapeAttribute(filters.toDate)}" />
            </label>
          </div>

          <div class="brief-filter-footer">
            <p>${escapeHtml(`${scopedNotes.length} signal${scopedNotes.length === 1 ? "" : "s"} included in this brief scope.`)}</p>
            <button class="brief-filter-reset" data-action="reset-brief-filters">Reset filters</button>
          </div>
        </section>

        <section class="brief-modal-summary brief-modal-summary-ai">
          <h3>AI Summary</h3>
          ${
            state.isLoadingBriefAiSummary
              ? `<p>Generating AI summary...</p>`
              : aiSummary
                ? `
                  <div class="brief-ai-grid">
                    <div>
                      <h4>Thesis</h4>
                      <p>${escapeHtml(aiSummary.thesis)}</p>
                    </div>
                    <div>
                      <h4>Why it matters</h4>
                      <p>${escapeHtml(aiSummary.whyItMatters)}</p>
                    </div>
                    <div>
                      <h4>What to watch</h4>
                      <ul>${aiSummary.whatToWatch.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>
                    </div>
                    <div>
                      <h4>Operator note</h4>
                      <p>${escapeHtml(aiSummary.operatorNote)}</p>
                    </div>
                  </div>
                `
                : `<p>Open the brief to generate its AI summary.</p>`
          }
        </section>

        <section class="brief-modal-summary">
          <h3>Executive summary</h3>
          <p>${escapeHtml(brief?.summary ?? "No brief has been generated yet.")}</p>
          <p>${escapeHtml(brief?.openingLine ?? "Add captured signals, then open the brief again.")}</p>
        </section>

        <section class="brief-modal-grid">
          <article class="brief-modal-card">
            <h3>Top signals</h3>
            ${
              topSignals.length
                ? `<ul>${topSignals
                    .map(
                      (note) =>
                        `<li><strong>${escapeHtml(note.title)}</strong><span>${escapeHtml(note.summary)}</span></li>`,
                    )
                    .join("")}</ul>`
                : `<p>No top signals yet.</p>`
            }
          </article>

          <article class="brief-modal-card">
            <h3>Watchlist</h3>
            ${
              watchlist.length
                ? `<ul>${watchlist
                    .map(
                      (note) =>
                        `<li><strong>${escapeHtml(note.title)}</strong><span>${escapeHtml(note.summary)}</span></li>`,
                    )
                    .join("")}</ul>`
                : `<p>No watchlist items yet.</p>`
            }
          </article>
        </section>

        <section class="brief-modal-grid">
          <article class="brief-modal-card">
            <h3>Themes</h3>
            ${
              themes.length
                ? `<div class="brief-theme-list">${themes
                    .map(
                      (theme) =>
                        `<span class="brief-theme-chip">${escapeHtml(theme.label)} · ${escapeHtml(String(theme.count))}</span>`,
                    )
                    .join("")}</div>`
                : `<p>No themes detected yet.</p>`
            }
          </article>

          <article class="brief-modal-card">
            <h3>Brief bullets</h3>
            ${
              brief?.bullets?.length
                ? `<ul>${brief.bullets.map((bullet) => `<li>${escapeHtml(bullet)}</li>`).join("")}</ul>`
                : `<p>No brief bullets yet.</p>`
            }
          </article>
        </section>

        <section class="brief-modal-card">
          <h3>References</h3>
          ${buildBriefSourcesMarkup(scopedNotes)}
        </section>
      </div>
    </section>
  `;
}

function buildSignalCard(note: PithyNote, selected: boolean): string {
  return `
    <article class="signal-card ${selected ? "is-selected" : ""}" data-note-id="${escapeAttribute(note.id)}">
      <div class="card-copy">
        <div class="card-meta">
          <span>${escapeHtml(note.source.label)}</span>
          <span>${escapeHtml(formatLongDate(note.capturedAt))}</span>
        </div>
        <h3>${escapeHtml(note.title)}</h3>
        <p class="card-summary">${escapeHtml(note.summary)}</p>
        <div class="card-footer">
          <span class="topic-pill">${escapeHtml(primaryTheme(note))}</span>
          <div class="card-status">
            <span class="severity-dot severity-dot-${escapeAttribute(note.signalStrength)}"></span>
            <span class="severity-text severity-text-${escapeAttribute(note.signalStrength)}">${escapeHtml(capitalize(note.signalStrength))}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="composer-secondary-button signal-card-action" data-note-id="${escapeAttribute(note.id)}">Open note</button>
          ${
            note.source.url
              ? `<a class="source-link signal-card-link" href="${escapeAttribute(note.source.url)}" target="_blank" rel="noreferrer">View source <span>${buildExternalIcon()}</span></a>`
              : `<span class="generated-pill">No source URL</span>`
          }
          <button class="detail-delete-button signal-card-delete" data-action="delete-note" data-note-id="${escapeAttribute(note.id)}">Delete</button>
        </div>
      </div>
    </article>
  `;
}

function getFilteredNotes(): PithyNote[] {
  const query = state.search.trim().toLowerCase();
  if (!query) {
    return state.notes;
  }

  return state.notes.filter((note) => {
    const haystack = [
      note.title,
      note.summary,
      note.source.label,
      note.category,
      note.riskType,
      ...(note.tags ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

function getSelectedNote(filteredNotes: PithyNote[]): PithyNote | null {
  return (
    filteredNotes.find((note) => note.id === state.selectedNoteId) ??
    state.notes.find((note) => note.id === state.selectedNoteId) ??
    null
  );
}

function getCreateSelectedNote(notes: PithyNote[]): PithyNote | null {
  return notes.find((note) => note.id === state.createComposer.sourceNoteId) ?? null;
}

function getWorkspaceTitle(view: SignalView): string {
  switch (view) {
    case "create":
      return "Signal Cards";
    case "capture":
      return "Inbox";
    case "outputs":
      return "Outputs";
    case "brief":
      return "Weekly Brief";
    case "enrichment":
      return "Pithy Signal";
    case "library":
      return "Library";
    case "profile":
      return "Profile";
    case "sources":
      return "Sources";
    default:
      return "Workspace";
  }
}

function buildCreatePreviewSlides(
  note: PithyNote | null,
  rawThought: string,
): CaptureOutput["slides"] {
  const fallback =
    summarizeCaptureText(rawThought) ||
    "Turn one rough idea into one piece of clear, usable value language.";
  const title = note?.title || fallback || "Start with a rough thought";
  const summary = note?.summary || fallback;
  const bullets = note?.slide.bullets.filter((bullet) => bullet.trim()) ?? [];

  return [
    { id: "slide-1", role: "hook", title, body: "Lead with the clearest idea or claim." },
    {
      id: "slide-2",
      role: "setup",
      title: "Set the context",
      body: summary || "Add one or two lines of context.",
    },
    {
      id: "slide-3",
      role: "insight-1",
      title: "First signal",
      body: bullets[0] || "Show the first concrete takeaway.",
    },
    {
      id: "slide-4",
      role: "insight-2",
      title: "How you think",
      body: bullets[1] || "Reveal the reasoning or pattern behind the idea.",
    },
    {
      id: "slide-5",
      role: "insight-3",
      title: "Why it matters",
      body: bullets[2] || "Connect the thought to a professional outcome.",
    },
    {
      id: "slide-6",
      role: "takeaway",
      title: "Close with value",
      body: "End with a sharp takeaway or next-step CTA.",
    },
  ];
}

function titleizeCaptureSlideLabel(role: CaptureOutput["slides"][number]["role"]): string {
  switch (role) {
    case "insight-1":
      return "Insight 1";
    case "insight-2":
      return "Insight 2";
    case "insight-3":
      return "Insight 3";
    default:
      return capitalize(role);
  }
}

function buildCreateRefinementButton(mode: CaptureRefinementMode, label: string): string {
  return `<button class="create-refinement-button" data-action="refine-create" data-refinement-mode="${escapeAttribute(mode)}" ${state.isGeneratingCreate ? "disabled" : ""}>${escapeHtml(label)}</button>`;
}

function buildCreateOutputProvenance(
  sourceNote: PithyNote | null,
  rawThought: string,
): CaptureOutputProvenance {
  if (sourceNote) {
    return {
      origin: "saved-note",
      sourceLabel: sourceNote.source.label || sourceNote.title,
      sourceKind: sourceNote.source.kind,
      sourceUrl: sourceNote.source.url,
      citation: sourceNote.source.citation,
      capturedAt: sourceNote.capturedAt,
      category: sourceNote.category,
      signalStrength: sourceNote.signalStrength,
      riskType: sourceNote.riskType,
      tags: sourceNote.tags,
      excerpt: sourceNote.selectedText || sourceNote.summary,
    };
  }

  return {
    origin: "pasted-thought",
    sourceLabel: "Typed directly into Create",
    excerpt: summarizeCaptureText(rawThought),
  };
}

function resolveOutputProvenance(output: CaptureOutput): CaptureOutputProvenance | null {
  if (output.provenance) {
    return output.provenance;
  }

  if (output.sourceNoteId) {
    const sourceNote = state.notes.find((note) => note.id === output.sourceNoteId) ?? null;
    if (sourceNote) {
      return buildCreateOutputProvenance(sourceNote, "");
    }
  }

  if (output.sourceSummary.trim()) {
    return {
      origin: "pasted-thought",
      sourceLabel: "Imported output without stored provenance",
      excerpt: output.sourceSummary.trim(),
    };
  }

  return null;
}

function buildCreateHistoryCard(output: CaptureOutput): string {
  return `
    <article class="create-history-card">
      <div class="create-history-card-top">
        <span class="generated-pill">${escapeHtml(formatCapturePlatformLabel(output.platform))}</span>
        <div class="create-history-card-meta">
          <span class="generated-pill">${escapeHtml(titleizePalette(output.palette))}</span>
          <span class="create-status-pill create-status-${escapeAttribute(output.status)}">${escapeHtml(titleizeCaptureStatus(output.status))}</span>
        </div>
      </div>
      <h5>${escapeHtml(output.title)}</h5>
      <p>${escapeHtml(output.sourceSummary)}</p>
      <div class="create-history-card-footer">
        <span>${escapeHtml(formatLongDate(output.updatedAt || output.createdAt))}</span>
        <div class="create-history-card-actions">
          <button class="create-history-action" data-action="load-create-output" data-output-id="${escapeAttribute(output.id)}">Open variant</button>
          <button class="create-history-action create-history-action-primary" data-action="branch-create-output" data-output-id="${escapeAttribute(output.id)}">Branch new draft</button>
          <button class="create-history-action" data-action="duplicate-create-output" data-output-id="${escapeAttribute(output.id)}">Duplicate</button>
        </div>
      </div>
    </article>
  `;
}

function buildOutputsLibraryCard(output: CaptureOutput): string {
  return `
    <article class="outputs-record-card">
      <div class="outputs-record-meta">
        <span>${escapeHtml(output.sourceTitle)}</span>
        <span>${escapeHtml(formatLongDate(output.updatedAt || output.createdAt))}</span>
      </div>
      <h4>${escapeHtml(output.title)}</h4>
      <p>${escapeHtml(output.sourceSummary)}</p>
        <div class="outputs-record-footer">
          <div class="outputs-record-badges">
            <span class="generated-pill">${escapeHtml(formatCapturePlatformLabel(output.platform))}</span>
            <span class="generated-pill">${escapeHtml(titleizePalette(output.palette))}</span>
            <span class="create-status-pill create-status-${escapeAttribute(output.status)}">${escapeHtml(titleizeCaptureStatus(output.status))}</span>
          </div>
          <div class="outputs-record-actions">
            <button class="composer-secondary-button" data-action="open-output-library-item" data-output-id="${escapeAttribute(output.id)}">Open in Create</button>
            <button class="composer-secondary-button" data-action="duplicate-create-output" data-output-id="${escapeAttribute(output.id)}">Duplicate</button>
            <button class="composer-secondary-button" data-action="delete-create-output" data-output-id="${escapeAttribute(output.id)}">Delete</button>
          </div>
        </div>
      </article>
    `;
}

function buildCreateStatusButton(status: CaptureOutputStatus): string {
  const isActive = state.createOutput?.status === status;
  return `<button class="create-status-button ${isActive ? "is-active" : ""}" data-action="set-create-status" data-output-status="${escapeAttribute(status)}">${escapeHtml(titleizeCaptureStatus(status))}</button>`;
}

function buildCreatePreviewTitle(platform: CapturePlatformMode): string {
  return platform === "none" ? "Signal Cards" : `${formatCapturePlatformLabel(platform)} Signal Cards`;
}

function buildCreateFooterLabel(platform: CapturePlatformMode): string {
  return platform === "none" ? "Pithy Signal" : `Pithy Signal · ${formatCapturePlatformLabel(platform)}`;
}

function formatCapturePlatformLabel(platform: CapturePlatformMode): string {
  switch (platform) {
    case "none":
      return "None";
    case "linkedin":
      return "LinkedIn";
    case "instagram":
      return "Instagram";
  }
}

function titleizeCaptureStatus(status: CaptureOutputStatus): string {
  return capitalize(status);
}

function humanizeRefinementMode(mode: CaptureRefinementMode): string {
  switch (mode) {
    case "more-practical":
      return "more practical";
    case "more-punchy":
      return "more punchy";
    case "more-professional":
      return "more professional";
    default:
      return mode;
  }
}

function normalizeResearchSourceUrls(sourceUrls: string[]): string[] {
  return Array.from(
    new Set(
      sourceUrls
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
}

function getResearchComposerSourceUrls(composer: ResearchComposerState): string[] {
  return normalizeResearchSourceUrls([...composer.sourceUrls, composer.sourceUrl]);
}

function isSupportedResearchDocument(file: File): boolean {
  if (file.type.startsWith("text/") || file.type === "application/json") {
    return true;
  }

  return /\.(csv|htm|html|json|log|md|markdown|txt|xml)$/i.test(file.name);
}

function inferDocumentType(filename: string): string {
  const extension = filename.split(".").pop()?.toLowerCase() ?? "";
  switch (extension) {
    case "md":
    case "markdown":
      return "text/markdown";
    case "csv":
      return "text/csv";
    case "html":
    case "htm":
      return "text/html";
    case "json":
      return "application/json";
    case "xml":
      return "application/xml";
    default:
      return "text/plain";
  }
}

function formatUploadedDocumentForResearchText(document: UploadedResearchDocument): string {
  return [
    `--- Document: ${document.name} ---`,
    document.text.trim(),
    `--- End Document: ${document.name} ---`,
  ]
    .filter(Boolean)
    .join("\n");
}

function formatFileSize(size: number): string {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function excerpt(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}...`;
}

function mergeCreateHistory(nextOutput: CaptureOutput, history: CaptureOutput[]): CaptureOutput[] {
  const deduped = [nextOutput, ...history.filter((item) => item.id !== nextOutput.id)];
  return deduped.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

function mergeLibraryRecords(
  record: ResearchLibraryRecord,
  records: ResearchLibraryRecord[],
): ResearchLibraryRecord[] {
  const deduped = [record, ...records.filter((item) => item.id !== record.id)];
  return deduped.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

function mergePrivateSources(
  sources: PrivateSourceRecord[],
  existingSources: PrivateSourceRecord[],
): PrivateSourceRecord[] {
  const ids = new Set(sources.map((source) => source.id));
  return [...sources, ...existingSources.filter((source) => !ids.has(source.id))].sort(
    (left, right) => right.updatedAt.localeCompare(left.updatedAt),
  );
}

function buildLibraryRow(record: ResearchLibraryRecord): string {
  const workflow = record.workflow ?? (record.assessmentReport ? "assessment" : "intelligence");
  const reportReady =
    workflow === "assessment"
      ? Boolean(record.assessmentReport)
      : Boolean(record.intelligenceBrief ?? record.brief);
  return `
    <div class="library-table library-table-row" role="row">
      <button class="library-open-button" data-action="open-library-record" data-record-id="${escapeAttribute(record.id)}">
        ${escapeHtml(record.title)}
      </button>
      <span>${reportReady ? "Ready" : "Empty"}</span>
      <span>${workflow === "assessment" ? "N/A" : record.script ? "Ready" : "Empty"}</span>
      <span>${workflow === "assessment" ? "N/A" : record.deepDive ? "Ready" : "Empty"}</span>
      <span>${escapeHtml(formatLongDate(record.updatedAt))}</span>
      <button class="library-delete-button library-export-button" data-action="download-library-record" data-record-id="${escapeAttribute(record.id)}">
        Download
      </button>
      <button class="library-delete-button" data-action="delete-library-record" data-record-id="${escapeAttribute(record.id)}">
        Delete
      </button>
    </div>
  `;
}

function buildCurrentResearchRecordSnapshot(): ResearchLibraryRecord {
  const now = new Date().toISOString();
  const isAssessment = state.researchComposer.workflow === "assessment";
  const sourceUrls = isAssessment ? [] : getResearchComposerSourceUrls(state.researchComposer);
  return {
    id: state.activeLibraryRecordId ?? "current",
    title:
      isAssessment
        ? state.assessmentComposer.workExample.trim() || "Untitled Pithy Signal Assessment"
        : state.researchComposer.title.trim() || "Untitled Pithy Signal",
    workflow: state.researchComposer.workflow,
    mode: state.researchComposer.mode,
    sourceUrl: sourceUrls[0],
    sourceUrls,
    paperText: isAssessment ? state.assessmentComposer.workExample : state.researchComposer.paperText,
    assessmentInput: isAssessment ? state.assessmentComposer : null,
    assessmentReport: state.assessmentReport,
    intelligenceBrief: state.researchBrief,
    brief: state.researchBrief,
    script: isAssessment ? null : state.researchScript,
    deepDive: isAssessment ? null : state.researchDeepDive,
    briefingVideo:
      state.libraryRecords.find((record) => record.id === state.activeLibraryRecordId)?.briefingVideo ??
      null,
    createdAt: now,
    updatedAt: now,
  };
}

function buildResearchRecordMarkdown(record: ResearchLibraryRecord): string {
  const sources = normalizeResearchSourceUrls(record.sourceUrls ?? [record.sourceUrl ?? ""]);
  const references = buildResearchReferences(record.title, sources, record.updatedAt);
  const workflow = record.workflow ?? (record.assessmentReport ? "assessment" : "intelligence");
  const sections = [
    `# ${record.title || "Untitled Pithy Signal"}`,
    "",
    "Exported from Pithy Signal Studio.",
    "",
    "## Record Details",
    "",
    `- Workflow: ${workflow}`,
    `- Mode: ${record.mode}`,
    `- Created: ${formatLongDate(record.createdAt)}`,
    `- Updated: ${formatLongDate(record.updatedAt)}`,
    record.briefingVideo?.url ? `- Legacy media file: ${record.briefingVideo.url}` : "",
    "",
    "## Sources",
    "",
    ...(sources.length ? sources.map((sourceUrl) => `- ${sourceUrl}`) : ["- No source URLs saved."]),
    "",
    "## References",
    "",
    ...(references.length ? references.map((reference) => `- ${reference}`) : ["- No references saved."]),
    "",
    "## Research Text",
    "",
    record.paperText.trim() || "_No research text saved._",
    "",
    workflow === "assessment" ? "## Assessment Report" : "## Intelligence Brief",
    "",
    ...(workflow === "assessment"
      ? formatAssessmentReportMarkdown(record.assessmentReport)
      : formatResearchBriefMarkdown(record.intelligenceBrief ?? record.brief ?? null)),
    "",
    "## Script",
    "",
    ...formatResearchScriptMarkdown(record.script),
    "",
    "## Deep Dive",
    "",
    ...formatResearchDeepDiveMarkdown(record.deepDive),
    "",
  ];

  return sections.join("\n");
}

function formatAssessmentReportMarkdown(report: PithySignalAssessmentReport | null): string[] {
  if (!report) {
    return ["_No assessment report generated yet._"];
  }

  return [
    "### Value Signal Summary",
    "",
    report.valueSignalSummary,
    "",
    "### OMAII Signal Classification",
    "",
    `- Observation: ${report.omaiiClassification.observation}`,
    `- Meaning: ${report.omaiiClassification.meaning}`,
    `- Action: ${report.omaiiClassification.action}`,
    `- Impact: ${report.omaiiClassification.impact}`,
    `- Intelligence: ${report.omaiiClassification.intelligence}`,
    "",
    "### Why This Matters",
    "",
    report.whyThisMatters,
    "",
    "### Actionable Insights",
    "",
    ...report.actionableInsights.map((insight) => `- ${insight}`),
    "",
    "### Stronger Language",
    "",
    `- Concise version: ${report.strongerLanguage.conciseVersion}`,
    `- Performance review version: ${report.strongerLanguage.performanceReviewVersion}`,
    `- Leadership update version: ${report.strongerLanguage.leadershipUpdateVersion}`,
    "",
    "### What to Save Next",
    "",
    ...report.evidenceToCapture.map((item) => `- ${item}`),
    "",
    "### Next Move",
    "",
    report.nextMove,
  ];
}

function formatResearchBriefMarkdown(brief: GovernanceResearchBrief | null): string[] {
  if (!brief) {
    return ["_No brief generated yet._"];
  }

  return [
    "### Signal Summary",
    "",
    brief.signalSummary,
    "",
    "### Pithy Insight",
    "",
    brief.pithyInsight,
    "",
    "### Why This Matters",
    "",
    brief.whyThisMatters,
    "",
    "### Action Triggers",
    "",
    ...brief.actionTriggers.map((trigger) => `- ${trigger}`),
    "",
    "### Governance Implications",
    "",
    brief.governanceImplications,
    "",
    "### TPRM Implications",
    "",
    brief.tprmImplications,
    "",
    "### Repeat Pattern Tag",
    "",
    brief.repeatPatternTag,
    "",
    "### Signal Strength",
    "",
    `${brief.signalStrength.rating} / 5`,
    "",
    brief.signalStrength.rationale,
    "",
    "### Build vs. Ignore",
    "",
    `${brief.buildVsIgnore.classification}: ${brief.buildVsIgnore.rationale}`,
    "",
    "### Suggested Pithy Notes Asset",
    "",
    brief.suggestedPithyNotesAsset,
    "",
    "### Source Notes",
    "",
    "#### Sourced Facts",
    "",
    ...brief.sourceNotes.sourcedFacts.map((item) => `- ${item}`),
    "",
    "#### Reasonable Analysis",
    "",
    ...brief.sourceNotes.reasonableAnalysis.map((item) => `- ${item}`),
    "",
    "#### Original Pithy Signal Interpretation",
    "",
    ...brief.sourceNotes.originalPithySignalInterpretation.map((item) => `- ${item}`),
  ];
}

function formatResearchScriptMarkdown(script: AvatarScript | null): string[] {
  if (!script) {
    return ["_No script generated yet._"];
  }

  return [
    `### ${script.title}`,
    "",
    "#### Hook",
    "",
    script.hook,
    "",
    "#### Sections",
    "",
    ...script.sections.map((section) => `- ${section}`),
    "",
    "#### Close",
    "",
    script.close,
    "",
    "#### Full Script",
    "",
    script.fullScript,
  ];
}

function formatResearchDeepDiveMarkdown(deepDive: DeepDiveScript | null): string[] {
  if (!deepDive) {
    return ["_No deep dive generated yet._"];
  }

  return [
    `### ${deepDive.title}`,
    "",
    "#### Executive Framing",
    "",
    deepDive.executiveFraming,
    "",
    ...deepDive.sections.flatMap((section) => [
      `#### ${section.heading}`,
      "",
      section.body,
      "",
    ]),
    "#### Practical Takeaways",
    "",
    ...deepDive.practicalTakeaways.map((takeaway) => `- ${takeaway}`),
    "",
    "#### Closing Perspective",
    "",
    deepDive.closingPerspective,
    "",
    "#### Full Text",
    "",
    deepDive.fullText,
  ];
}

function getFilteredLibraryRecords(): ResearchLibraryRecord[] {
  const query = state.librarySearch.trim().toLowerCase();
  if (!query) {
    return state.libraryRecords;
  }

  return state.libraryRecords.filter((record) =>
    [record.title, ...(record.sourceUrls ?? [record.sourceUrl ?? ""]), record.mode]
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
}

function getFilteredOutputs(): CaptureOutput[] {
  const query = state.search.trim().toLowerCase();
  const filtered = state.outputsLibrary.filter((output) => {
    const matchesStatus =
      state.outputsFilters.status === "all" || output.status === state.outputsFilters.status;
    const matchesPlatform =
      state.outputsFilters.platform === "all" ||
      output.platform === state.outputsFilters.platform;
    const matchesPalette =
      state.outputsFilters.palette === "all" || output.palette === state.outputsFilters.palette;
    const matchesQuery =
      !query ||
      [
        output.title,
        output.sourceTitle,
        output.sourceSummary,
        output.platform,
        output.palette,
        output.status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);

    return matchesStatus && matchesPlatform && matchesPalette && matchesQuery;
  });

  switch (state.outputsFilters.sort) {
    case "oldest":
      return [...filtered].sort((left, right) => left.updatedAt.localeCompare(right.updatedAt));
    case "published-first":
      return [...filtered].sort((left, right) => {
        const leftRank = left.status === "published" ? 0 : left.status === "ready" ? 1 : 2;
        const rightRank = right.status === "published" ? 0 : right.status === "ready" ? 1 : 2;
        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }
        return right.updatedAt.localeCompare(left.updatedAt);
      });
    case "source-title":
      return [...filtered].sort((left, right) => left.sourceTitle.localeCompare(right.sourceTitle));
    default:
      return [...filtered].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }
}

function getCreatePaletteTheme(palette: PithyPaletteId): {
  background: string;
  surface: string;
  foreground: string;
  muted: string;
  accent: string;
  border: string;
  chip: string;
} {
  switch (palette) {
    case "editorial":
      return {
        background: "#fff7ef",
        surface: "#f7f1e8",
        foreground: "#2e2620",
        muted: "#8b6f5e",
        accent: "#bf5c34",
        border: "#e8d7c8",
        chip: "#f2dfcf",
      };
    case "punch":
      return {
        background: "#fff1df",
        surface: "#ffe2c2",
        foreground: "#1f1f1f",
        muted: "#8c5f3d",
        accent: "#d46a1f",
        border: "#f0d1b1",
        chip: "#ffd2a2",
      };
    default:
      return {
        background: "#f2f6ff",
        surface: "#eaf0ff",
        foreground: "#15233a",
        muted: "#5c708f",
        accent: "#315ea8",
        border: "#d7e2f5",
        chip: "#dce6fb",
      };
  }
}

function primaryTheme(note: PithyNote): string {
  return note.category === "AI governance"
    ? "Policy & regulation"
    : note.category === "third-party risk"
      ? "Semiconductors"
      : note.category === "communication design"
        ? "Energy transition"
        : note.category;
}

function buildThemeLabels(note: PithyNote): string[] {
  const labels = new Set<string>();
  labels.add(primaryTheme(note));
  if (note.riskType === "vendor") {
    labels.add("Investment");
  }
  if (note.riskType === "regulatory") {
    labels.add("Clean tech");
  }
  if (note.riskType === "operational") {
    labels.add("Global markets");
  }
  for (const tag of note.tags ?? []) {
    if (labels.size >= 4) {
      break;
    }
    labels.add(titleizeTag(tag));
  }
  return [...labels].slice(0, 4);
}

function buildInsightRow(label: string, value: string, icon: "star" | "gear" | "watch"): string {
  return `
    <article class="insight-row">
      <div class="insight-icon">${buildInsightIcon(icon)}</div>
      <div class="insight-copy">
        <h4>${escapeHtml(label)}</h4>
        <p>${escapeHtml(value)}</p>
      </div>
    </article>
  `;
}

function buildKeyTakeaway(note: PithyNote): string {
  return note.summary;
}

function buildWhyItMatters(note: PithyNote): string {
  if (note.slide.bullets[1]) {
    return note.slide.bullets[1];
  }
  return `Sustained movement in ${note.category.toLowerCase()} signals a material shift in operating conditions.`;
}

function titleizePalette(value: PithyPaletteId): string {
  return capitalize(value);
}

function buildWhatToWatch(note: PithyNote): string {
  if (note.tags?.length) {
    return `Watch ${note.tags.slice(0, 3).join(", ")}, plus follow-on changes in ${note.riskType} posture.`;
  }
  return `Watch follow-on developments tied to ${note.riskType} exposure and decision timing.`;
}

function buildBriefMeta(brief: WeeklyBrief | null, noteCount: number): string {
  const themeCount = brief?.themes.length ?? 0;
  const insightCount = brief?.bullets.length ? Math.max(brief.bullets.length - 1, 0) : 0;
  return `${noteCount} signals · ${insightCount} insights · ${themeCount} themes`;
}

function buildBriefDateLine(brief: WeeklyBrief | null): string {
  if (!brief) {
    return "Weekly brief not generated yet";
  }
  return `Week of ${brief.dateLabel}`;
}

function getThumbnailClass(note: PithyNote): string {
  if (note.riskType === "regulatory") {
    return "thumb-government";
  }
  if (note.riskType === "vendor") {
    return "thumb-tech";
  }
  if (note.category === "communication design") {
    return "thumb-energy";
  }
  return "thumb-shipping";
}

function buildThumbnailArt(note: PithyNote): string {
  const variant = getThumbnailClass(note);
  if (variant === "thumb-government") {
    return `<svg viewBox="0 0 160 112" aria-hidden="true"><rect width="160" height="112" fill="#dce8ef"/><rect y="82" width="160" height="30" fill="#c9d4db"/><rect x="60" y="32" width="40" height="42" fill="#f4f4f2"/><path d="M80 18 L118 34 H42 Z" fill="#f4f4f2"/><rect x="50" y="38" width="6" height="36" fill="#d2d7dc"/><rect x="68" y="38" width="6" height="36" fill="#d2d7dc"/><rect x="86" y="38" width="6" height="36" fill="#d2d7dc"/><rect x="104" y="38" width="6" height="36" fill="#d2d7dc"/></svg>`;
  }
  if (variant === "thumb-tech") {
    return `<svg viewBox="0 0 160 112" aria-hidden="true"><rect width="160" height="112" fill="#122535"/><rect x="42" y="26" width="76" height="60" rx="8" fill="#304c65"/><rect x="58" y="42" width="44" height="28" rx="4" fill="#d9e7ef"/><path d="M52 18 V30 M68 18 V30 M84 18 V30 M100 18 V30 M116 18 V30 M52 82 V94 M68 82 V94 M84 82 V94 M100 82 V94 M116 82 V94" stroke="#8fb2c8" stroke-width="4" stroke-linecap="round"/></svg>`;
  }
  if (variant === "thumb-shipping") {
    return `<svg viewBox="0 0 160 112" aria-hidden="true"><rect width="160" height="112" fill="#dce7ef"/><rect y="72" width="160" height="40" fill="#9bb1bf"/><rect x="20" y="46" width="28" height="20" fill="#c16d3b"/><rect x="48" y="46" width="28" height="20" fill="#b8562e"/><rect x="76" y="46" width="28" height="20" fill="#d89b54"/><rect x="104" y="46" width="28" height="20" fill="#b8562e"/><path d="M38 30 V46 M68 26 V46 M98 22 V46" stroke="#546776" stroke-width="4"/><path d="M38 30 L54 18 M68 26 L84 14 M98 22 L114 10" stroke="#546776" stroke-width="3"/></svg>`;
  }
  return `<svg viewBox="0 0 160 112" aria-hidden="true"><rect width="160" height="112" fill="#ddd7cb"/><rect y="82" width="160" height="30" fill="#b7b3a6"/><path d="M36 74 L52 40 L68 74 M96 74 L112 28 L128 74" stroke="#f6f6f3" stroke-width="4" fill="none"/><path d="M52 48 C62 36 76 36 88 48 C78 56 62 56 52 48Z" fill="#eef5f8"/></svg>`;
}

function buildSidebarIcon(kind: string): string {
  if (kind === "pulse") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h4l2.2-5.5L13 17l2.1-5H21" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  if (kind === "inbox") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5v9A1.5 1.5 0 0 1 18.5 18h-13A1.5 1.5 0 0 1 4 16.5z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M4 13h4l1.5 2h5L16 13h4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>`;
  }
  if (kind === "brief") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="4" width="14" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>`;
  }
  if (kind === "spark") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM18 16l.8 2.2L21 19l-2.2.8L18 22l-.8-2.2L15 19l2.2-.8z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
  }
  if (kind === "tag") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11V5h6l9 9-6 6z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><circle cx="8" cy="8" r="1.4" fill="currentColor"/></svg>`;
  }
  if (kind === "doc") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h7l4 4v12H7z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M14 4v4h4M9 13h6M9 17h6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>`;
  }
  if (kind === "archive") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="4" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M6 9h12v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M10 13h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>`;
  }
  if (kind === "user") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM5 20a7 7 0 0 1 14 0" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  if (kind === "logout") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 17l5-5-5-5M20 12H9M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 7h8M9 7v11m6-11v11M5 7h14M7 7l1-2h8l1 2M6 7l1 12h10l1-12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildAvatarMark(): string {
  return `<svg viewBox="0 0 40 40" aria-hidden="true"><defs><linearGradient id="avatar-grad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#e8d3c1"/><stop offset="1" stop-color="#8f5a3b"/></linearGradient></defs><circle cx="20" cy="20" r="20" fill="url(#avatar-grad)"/><circle cx="20" cy="15" r="6" fill="#f7efe6"/><path d="M10 33c2-6 7-9 10-9s8 3 10 9" fill="#213243"/></svg>`;
}

function buildSearchIcon(): string {
  return `<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="4.8" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M12.2 12.2 L16 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
}

function buildCaretIcon(): string {
  return `<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M3 5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildListIcon(): string {
  return `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 6h10M8 10h7M11 14h4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
}

function buildBookmarkIcon(): string {
  return `<svg viewBox="0 0 18 18" aria-hidden="true"><path d="M5 3.5h8a1 1 0 0 1 1 1v10l-5-2.7L4 14.5v-10a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`;
}

function buildBookmarkOutlineIcon(): string {
  return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 3.2h7a.8.8 0 0 1 .8.8v8.2L8 10l-4.3 2.2V4a.8.8 0 0 1 .8-.8z" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linejoin="round"/></svg>`;
}

function buildDotsIcon(): string {
  return `<svg viewBox="0 0 18 18" aria-hidden="true"><circle cx="4" cy="9" r="1.3" fill="currentColor"/><circle cx="9" cy="9" r="1.3" fill="currentColor"/><circle cx="14" cy="9" r="1.3" fill="currentColor"/></svg>`;
}

function buildExternalIcon(): string {
  return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M9.5 3.5H13v3.5M12.8 3.7 7 9.5" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/><path d="M7 4H4.5A1.5 1.5 0 0 0 3 5.5v6A1.5 1.5 0 0 0 4.5 13h6A1.5 1.5 0 0 0 12 11.5V9" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>`;
}

function buildSparkIcon(): string {
  return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l1.1 3.1L12 6.2 8.9 7.3 8 10.5 6.9 7.3 3.8 6.2 6.9 5.1z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>`;
}

function buildInsightIcon(kind: "star" | "gear" | "watch"): string {
  if (kind === "gear") {
    return `<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="2.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M10 2.8v2M10 15.2v2M17.2 10h-2M4.8 10h-2M15.1 4.9l-1.4 1.4M6.3 13.7l-1.4 1.4M15.1 15.1l-1.4-1.4M6.3 6.3 4.9 4.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`;
  }
  if (kind === "watch") {
    return `<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M10 6.7v3.6l2.4 1.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  return `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 2.5l1.6 4.2 4.2 1.6-4.2 1.6L10 14.1l-1.6-4.2-4.2-1.6 4.2-1.6z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`;
}

function buildArrowRightIcon(): string {
  return `<svg viewBox="0 0 18 18" aria-hidden="true"><path d="M4 9h10M10 5l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildDownloadIcon(): string {
  return `<svg viewBox="0 0 18 18" aria-hidden="true"><path d="M9 3.5v7M6.2 8.8 9 11.6l2.8-2.8M4 14h10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildDownIcon(): string {
  return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6.5 8 10.5l4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildWeeklyBriefMarkdown(brief: WeeklyBrief | null, notes: PithyNote[]): string {
  if (!brief) {
    return [
      "# Pithy Notes Signal Weekly Brief",
      "",
      "No weekly brief is available yet.",
      "",
      "Capture a few signals and try again.",
      "",
    ].join("\n");
  }

  const sections = [
    `# ${brief.title}`,
    "",
    `Date: ${brief.dateLabel}`,
    `Saved signals: ${notes.length}`,
    "",
    "## Executive Summary",
    "",
    brief.summary,
    "",
    brief.openingLine,
    "",
    "## Top Signals",
    "",
    ...formatBriefNotesMarkdown(brief.topSignals),
    "",
    "## Watchlist",
    "",
    ...(brief.watchlist.length
      ? formatBriefNotesMarkdown(brief.watchlist)
      : ["- No watchlist items yet."]),
    "",
    "## Themes",
    "",
    ...(brief.themes.length
      ? brief.themes.map((theme) => `- ${theme.label} (${theme.count})`)
      : ["- No themes detected yet."]),
    "",
    "## Brief Bullets",
    "",
    ...(brief.bullets.length
      ? brief.bullets.map((bullet) => `- ${bullet}`)
      : ["- No brief bullets yet."]),
    "",
    "## Action Counts",
    "",
    `- Act: ${brief.actionCounts.act}`,
    `- Prepare: ${brief.actionCounts.prepare}`,
    `- Monitor: ${brief.actionCounts.monitor}`,
    "",
    "## Signal Counts",
    "",
    `- High: ${brief.signalCounts.high}`,
    `- Medium: ${brief.signalCounts.medium}`,
    `- Low: ${brief.signalCounts.low}`,
    "",
    "## Sources",
    "",
    ...formatBriefSourcesMarkdown(notes),
    "",
  ];

  return sections.join("\n");
}

function getCurrentBriefScope(): { brief: WeeklyBrief; notes: PithyNote[]; scopeKey: string } {
  const notes = getNotesForBriefFilters();
  return {
    brief: buildWeeklyBrief(notes),
    notes,
    scopeKey: JSON.stringify({
      filters: state.briefFilters,
      noteIds: notes.map((note) => note.id),
    }),
  };
}

function getNotesForBriefFilters(): PithyNote[] {
  const { category, tag, fromDate, toDate } = state.briefFilters;

  return state.notes.filter((note) => {
    const matchesCategory = !category || note.category === category;
    const matchesTag = !tag || (note.tags ?? []).includes(tag);
    const noteDate = note.capturedAt.slice(0, 10);
    const matchesFrom = !fromDate || noteDate >= fromDate;
    const matchesTo = !toDate || noteDate <= toDate;
    return matchesCategory && matchesTag && matchesFrom && matchesTo;
  });
}

function getBriefFilterCategories(): string[] {
  return [...new Set(state.notes.map((note) => note.category).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right),
  );
}

function getBriefFilterTags(): string[] {
  return [...new Set(state.notes.flatMap((note) => note.tags ?? []).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right),
  );
}

function getSourceRecords(
  notes: PithyNote[],
  libraryRecords: ResearchLibraryRecord[] = [],
): SourceRecord[] {
  const uniqueByKey = new Map<string, SourceRecord>();

  for (const note of notes) {
    const key = [
      note.source.url?.trim().toLowerCase() || "",
      note.title.trim().toLowerCase(),
      note.capturedAt.slice(0, 10),
    ].join("::");
    if (uniqueByKey.has(key)) {
      continue;
    }

    uniqueByKey.set(key, {
      id: key,
      title: note.title,
      sourceLabel: note.source.label,
      sourceKind: note.source.kind,
      url: note.source.url,
      capturedAt: note.capturedAt,
      citation: note.source.citation?.trim() || buildFallbackCitation(note),
    });
  }

  for (const record of libraryRecords) {
    const sourceUrls = normalizeResearchSourceUrls(record.sourceUrls ?? [record.sourceUrl ?? ""]);
    for (const sourceUrl of sourceUrls) {
      const key = sourceUrl.trim().toLowerCase()
        ? ["library", sourceUrl.trim().toLowerCase()].join("::")
        : ["library", record.id].join("::");
      if (uniqueByKey.has(key)) {
        continue;
      }

      uniqueByKey.set(key, {
        id: key,
        title: record.title,
        sourceLabel: "Library record",
        sourceKind: "reference",
        url: sourceUrl,
        capturedAt: record.updatedAt,
        citation: formatSourceUrlCitation({
          title: record.title,
          url: sourceUrl,
          retrievedAt: record.updatedAt,
        }),
      });
    }
  }

  return [...uniqueByKey.values()].sort((left, right) =>
    right.capturedAt.localeCompare(left.capturedAt),
  );
}

function buildBriefSourcesMarkup(notes: PithyNote[]): string {
  const sourceRecords = getSourceRecords(notes);
  if (!sourceRecords.length) {
    return `<p>No source citations are available for this brief yet.</p>`;
  }

  return `
    <ol class="brief-sources-list">
      ${sourceRecords
        .map(
          (record) => `
            <li>
              <p>${escapeHtml(record.citation)}</p>
            </li>
          `,
        )
        .join("")}
    </ol>
  `;
}

function formatBriefSourcesMarkdown(notes: PithyNote[]): string[] {
  const sourceRecords = getSourceRecords(notes);
  if (!sourceRecords.length) {
    return ["- No source citations available yet."];
  }

  return sourceRecords.map((record) => `- ${record.citation}`);
}

function getCurrentResearchReferences(): string[] {
  const sourceUrls = getResearchComposerSourceUrls(state.researchComposer);
  return buildResearchReferences(state.researchComposer.title, sourceUrls);
}

function buildResearchReferences(title: string, sourceUrls: string[], retrievedAt?: string): string[] {
  return normalizeResearchSourceUrls(sourceUrls).map((sourceUrl) =>
    formatSourceUrlCitation({
      title: title.trim() || "Untitled Pithy Signal",
      url: sourceUrl,
      retrievedAt,
    })
  );
}

function buildFallbackCitation(note: PithyNote): string {
  const author = note.source.label.trim() || "Unknown source";
  const title = note.title.trim() || "Untitled signal";
  const url = note.source.url?.trim();
  return [author, title, url].filter(Boolean).join(". ");
}

function formatBriefNotesMarkdown(notes: PithyNote[]): string[] {
  return notes.flatMap((note) => {
    const lines = [
      `- **${note.title}**`,
      `  - Summary: ${note.summary}`,
      `  - Category: ${note.category}`,
      `  - Risk: ${note.riskType}`,
      `  - Suggested action: ${note.suggestedAction}`,
    ];

    if (note.tags?.length) {
      lines.push(`  - Tags: ${note.tags.join(", ")}`);
    }

    if (note.source.url) {
      lines.push(`  - Source: ${note.source.url}`);
    }

    return lines;
  });
}

function downloadTextFile(filename: string, contents: string, mimeType: string): void {
  const blob = new Blob([contents], { type: mimeType });
  downloadBlob(filename, blob);
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "pithy-notes-weekly-brief"
  );
}

function createEmptyCaptureComposer(): CaptureComposerState {
  return {
    title: "",
    sourceUrl: "",
    sourceKind: "article",
    capturedText: "",
    category: "",
    summary: "",
    signalStrength: "medium",
    riskType: "operational",
    suggestedAction: "prepare",
    tags: "",
    slide: buildFallbackSlide("", "prepare"),
  };
}

function createEmptyResearchComposer(): ResearchComposerState {
  return {
    workflow: "intelligence",
    mode: "general",
    title: "",
    sourceUrl: "",
    sourceUrls: [],
    paperText: "",
    documents: [],
    privateSourceQuery: "",
    scholarQuery: "",
  };
}

function createEmptyAssessmentComposer(): AssessmentComposerState {
  return {
    workExample: "",
    whatUserDid: "",
    howUserDidIt: "",
    stakeholdersInvolved: "",
    businessImpact: "",
    aiUsed: "",
    currentStatus: "",
    evidenceAvailable: "",
    desiredUseCase: "",
  };
}

function createEmptyCreateComposer(): CreateComposerState {
  return {
    sourceMode: "existing-note",
    sourceNoteId: "",
    rawThought: "",
    platform: "linkedin",
    palette: "signal",
  };
}

function createEmptyPublishReceipt(): CreatePublishReceiptState {
  return {
    reviewerName: "",
    approvalNote: "",
  };
}

function toCreatePublishReceiptState(
  receipt?: CapturePublishReceipt | CapturePublishReceiptInput | null,
): CreatePublishReceiptState {
  return {
    reviewerName: receipt?.reviewerName ?? "",
    approvalNote: receipt?.approvalNote ?? "",
  };
}

function buildDraftPublishReceipt(): CapturePublishReceiptInput | null {
  const reviewerName = state.createPublishReceipt.reviewerName.trim();
  const approvalNote = state.createPublishReceipt.approvalNote.trim();
  if (!reviewerName && !approvalNote) {
    return null;
  }

  return {
    reviewerName,
    approvalNote,
  };
}

function buildTrustPanel(context: "auth" | "create" | "capture"): string {
  const heading =
    context === "auth"
      ? "Built for visible thinking, with clear privacy boundaries."
      : context === "capture"
        ? "Keep sensitive details out of the workspace."
      : "Generate only from content you are comfortable using.";
  const intro =
    context === "auth"
      ? "You should always know whether this workspace is hosted or local, when model providers are involved, and how to export or delete what you create."
      : context === "capture"
        ? "Use non-confidential notes, examples, and reflections. Save only what you are comfortable keeping in this workspace."
      : "Signal Cards should use your own non-confidential work examples, notes, and reflections.";

  return `
    <section class="trust-panel trust-panel-${context}">
      <div class="trust-panel-header">
        <p class="trust-panel-kicker">Workspace reminder</p>
        <h3>${escapeHtml(heading)}</h3>
        <p>${escapeHtml(intro)}</p>
      </div>
      <div class="trust-card-grid">
        ${buildTrustCards(context)
          .map(
            (card) => `
              <article class="trust-card">
                <p class="trust-card-label">${escapeHtml(card.label)}</p>
                <h4>${escapeHtml(card.title)}</h4>
                <p>${escapeHtml(card.body)}</p>
              </article>
            `,
          )
          .join("")}
      </div>
    </section>
  `;
}

function buildTrustCards(
  context: "auth" | "create" | "capture",
): Array<{ label: string; title: string; body: string }> {
  const workspaceIsCloud = state.session.workspaceMode === "cloud";
  const aiUsesProvider = state.session.aiUsageMode === "provider";
  const aiProvider = state.session.aiProviderLabel ?? "OpenAI";
  const aiUsesUserKey = state.session.aiCredentialMode === "user";
  const baseCards = [
    {
      label: "Workspace",
      title: workspaceIsCloud ? "Cloud workspace" : "Local workspace",
      body: workspaceIsCloud
        ? "Your notes and generated outputs are stored in your hosted Pithy Notes workspace."
        : "Your notes and generated outputs stay on this device unless you explicitly choose to sync or export them.",
    },
    {
      label: "AI usage",
      title: aiUsesProvider ? `${aiProvider} in use` : "Local heuristics active",
      body: aiUsesProvider
        ? aiUsesUserKey
          ? `When AI generation is used, ${
              context === "capture" ? "captured source content" : "selected source content"
            } may be sent to ${aiProvider} using your browser-session key. Pithy Notes does not store that key in the workspace.`
          : `When AI generation is used, ${
              context === "capture" ? "captured source content" : "the selected source content"
            } may be sent to ${aiProvider} to create your draft output.`
        : "This deployment is not configured to send content to a model provider, so generation falls back to local heuristics instead.",
    },
  ];

  if (context === "capture") {
    return [
      ...baseCards,
      {
        label: "Draft state",
        title: "Raw capture stays in the composer until you save it",
        body: "Pasted text, tags, and notes stay as an in-browser draft first. They become a saved Signal artifact only after you choose Save to inbox.",
      },
      {
        label: "Artifact boundary",
        title: workspaceIsCloud
          ? "Saved notes stay in the hosted workspace until you export them"
          : "Saved notes stay on this device until you export them",
        body: "Enhance with AI can help structure the draft, but it does not publish or export anything. Review the saved note before you turn it into a brief, post, or exported file.",
      },
    ];
  }

  if (context === "create") {
    return [
      ...baseCards,
      {
        label: "Output state",
        title: "Generated drafts are still operator-reviewed work",
        body: "Create gives you a reviewable draft, not an automatically published asset. Keep editing, change status intentionally, and add a publish receipt before treating it as final.",
      },
      {
        label: "Artifact boundary",
        title: state.session.exportEnabled
          ? "Saved outputs stay portable, but export is a separate step"
          : "Export controls are limited in this deployment",
        body: state.session.exportEnabled
          ? "Your generated output stays inside the workspace until you explicitly export a PNG set, PDF, or caption."
          : "This deployment currently limits export controls, so review the host policy before using it for important work.",
      },
    ];
  }

  return [
    ...baseCards,
    {
      label: "Portability",
      title: state.session.exportEnabled
        ? "Export your work whenever you want"
        : "Export controls are limited in this deployment",
      body: state.session.exportEnabled
        ? "Notes, captions, and generated outputs stay portable through the app's export actions."
        : "This deployment currently limits export controls, so review the host policy before using it for important work.",
    },
    {
      label: "Control",
      title: state.session.deleteEnabled
        ? workspaceIsCloud
          ? "Delete hosted work when you no longer need it"
          : "Delete local work when you no longer need it"
        : "Deletion controls are limited in this deployment",
      body: state.session.deleteEnabled
        ? workspaceIsCloud
          ? "Delete notes and generated outputs from your hosted workspace, and plan for a local-first path if you need stronger privacy controls later."
          : "Delete notes and generated outputs from this device, with local control preserved by default."
        : "This deployment does not currently expose deletion controls in the product surface.",
    },
  ];
}

function openCreateWorkspace(noteId: string): void {
  const selected = state.notes.find((note) => note.id === noteId) ?? null;
  state = {
    ...state,
    activeView: "create",
    createComposer: {
      ...state.createComposer,
      sourceMode: selected ? "existing-note" : "paste",
      sourceNoteId: selected?.id ?? "",
      rawThought: selected ? "" : state.createComposer.rawThought,
      platform: "linkedin",
      palette: "signal",
    },
    createPublishReceipt: createEmptyPublishReceipt(),
    createOutput: null,
    createHistory: [],
    isLoadingCreateHistory: Boolean(selected?.id),
    createMessage: selected
      ? "Source note loaded into Pithy Signal."
      : "Choose a source note or paste a rough thought to begin.",
  };
  if (window.location.hash.replace(/^#/, "") !== "create") {
    window.location.hash = "create";
  }
  render();
  if (selected?.id) {
    void loadCreateHistory(selected.id);
  }
}

function buildSelectOptions(options: Array<[string, string]>, selectedValue: string): string {
  return options
    .map(
      ([value, label]) =>
        `<option value="${escapeAttribute(value)}" ${value === selectedValue ? "selected" : ""}>${escapeHtml(label)}</option>`,
    )
    .join("");
}

function buildSourceKindOptions(selectedValue: PithyNote["source"]["kind"]): string {
  return buildSelectOptions(
    [
      ["article", "Article"],
      ["linkedin", "LinkedIn"],
      ["vendor-doc", "Vendor doc"],
      ["meeting-note", "Meeting note"],
      ["research-note", "Research note"],
      ["other", "Other"],
    ],
    selectedValue,
  );
}

function titleizeSourceKind(value: string): string {
  switch (value) {
    case "vendor-doc":
      return "Vendor doc";
    case "meeting-note":
      return "Meeting note";
    case "research-note":
      return "Research note";
    case "reference":
      return "Reference";
    default:
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }
}

function buildNoteFromComposer(composer: CaptureComposerState): PithyNote {
  const summary = composer.summary.trim() || summarizeCaptureText(composer.capturedText);
  const suggestedAction = composer.suggestedAction;
  return {
    id: `PN-${Date.now()}`,
    title: composer.title.trim() || "Manual signal capture",
    capturedAt: new Date().toISOString(),
    source: {
      label: composer.title.trim() || "Manual capture",
      kind: composer.sourceKind,
      url: composer.sourceUrl.trim() || undefined,
    },
    selectedText: composer.capturedText.trim(),
    summary,
    signalStrength: composer.signalStrength,
    riskType: composer.riskType,
    category: composer.category.trim() || "general",
    suggestedAction,
    tags: composer.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    slide: hasMeaningfulSlide(composer.slide)
      ? composer.slide
      : buildFallbackSlide(summary, suggestedAction),
  };
}

function summarizeCaptureText(value: string): string {
  const cleaned = value.replaceAll(/\s+/g, " ").trim();
  if (!cleaned) {
    return "";
  }
  const sentence = cleaned.split(/(?<=[.!?])\s+/)[0] ?? cleaned;
  return sentence.length > 190 ? `${sentence.slice(0, 187)}...` : sentence;
}

function buildFallbackSlide(
  summary: string,
  action: PithyNote["suggestedAction"],
): PithyNote["slide"] {
  return {
    headline: summary
      ? `${summary} ${action === "act" ? "Needs action." : "Needs attention."}`
      : "Signal captured",
    bullets: [
      "Captured directly inside Pithy Notes Signal.",
      `Recommended posture: ${action}.`,
      "Ready for executive review and weekly briefing.",
    ],
    speakerNote: "Use this as a first-pass framing draft before polishing the brief.",
  };
}

function hasMeaningfulSlide(slide: PithyNote["slide"]): boolean {
  return Boolean(
    slide.headline.trim() ||
    slide.speakerNote.trim() ||
    slide.bullets.some((bullet) => bullet.trim()),
  );
}

function formatLongDate(value: string): string {
  const parsed = new Date(value);
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLongDateTime(value: string): string {
  const parsed = new Date(value);
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function titleizeTag(value: string): string {
  return value
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((part) => capitalize(part))
    .join(" ");
}

function buildRequestHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);
  const userApiKey = state.userApiKey.trim();
  const embeddingApiKey = state.embeddingApiKey.trim();
  const scholarAiApiKey = state.scholarAiApiKey.trim();
  const regulationsApiKey = state.regulationsApiKey.trim();
  const nvdApiKey = state.nvdApiKey.trim();
  headers.set("x-pithy-signal-ai-provider", state.userAiProvider);
  if (userApiKey) {
    headers.set("x-pithy-signal-user-api-key", userApiKey);
  } else {
    headers.delete("x-pithy-signal-user-api-key");
  }
  setOptionalHeader(headers, "x-pithy-signal-embedding-api-key", embeddingApiKey);
  setOptionalHeader(headers, "x-pithy-signal-scholarai-api-key", scholarAiApiKey);
  setOptionalHeader(headers, "x-pithy-signal-regulations-api-key", regulationsApiKey);
  setOptionalHeader(headers, "x-pithy-signal-nvd-api-key", nvdApiKey);
  return headers;
}

function setOptionalHeader(headers: Headers, name: string, value: string): void {
  if (value) {
    headers.set(name, value);
    return;
  }
  headers.delete(name);
}

async function requestJson<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    headers: buildRequestHeaders(init),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `Request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function wrapSvgText(value: string, maxChars: number): string[] {
  const words = value.replaceAll(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (!words.length) {
    return [""];
  }

  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      continue;
    }
    if (current) {
      lines.push(current);
    }
    current = word;
  }
  if (current) {
    lines.push(current);
  }

  return lines;
}

function buildCreateExportSvg(
  output: CaptureOutput,
  slide: CaptureOutput["slides"][number],
  index: number,
): string {
  const palette = getCreatePaletteTheme(output.palette);
  const titleLines = wrapSvgText(slide.title, 18).slice(0, 4);
  const bodyLines = wrapSvgText(slide.body, output.platform === "instagram" ? 24 : 28).slice(0, 8);

  const titleText = titleLines
    .map(
      (line, lineIndex) =>
        `<tspan x="88" dy="${lineIndex === 0 ? 0 : 96}">${escapeXml(line)}</tspan>`,
    )
    .join("");
  const bodyText = bodyLines
    .map(
      (line, lineIndex) =>
        `<tspan x="88" dy="${lineIndex === 0 ? 0 : 56}">${escapeXml(line)}</tspan>`,
    )
    .join("");

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
      <defs>
        <linearGradient id="cardGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${palette.background}" />
          <stop offset="100%" stop-color="${palette.surface}" />
        </linearGradient>
      </defs>
      <rect width="1080" height="1350" fill="url(#cardGradient)" />
      <rect x="40" y="40" width="1000" height="1270" rx="34" fill="none" stroke="${palette.border}" stroke-width="2" />
      <rect x="88" y="92" width="180" height="44" rx="22" fill="${palette.chip}" />
      <text x="88" y="120" fill="${palette.muted}" font-size="28" font-family="Avenir Next, Segoe UI, Arial, sans-serif" letter-spacing="2">SLIDE ${index + 1}</text>
      <text x="992" y="120" text-anchor="end" fill="${palette.muted}" font-size="28" font-family="Avenir Next, Segoe UI, Arial, sans-serif" letter-spacing="2">${escapeXml(titleizeCaptureSlideLabel(slide.role).toUpperCase())}</text>
      <text x="88" y="320" fill="${palette.foreground}" font-size="92" font-weight="700" font-family="Avenir Next, Segoe UI, Arial, sans-serif" letter-spacing="-3">${titleText}</text>
      <text x="88" y="760" fill="${palette.foreground}" font-size="44" font-family="Avenir Next, Segoe UI, Arial, sans-serif">${bodyText}</text>
      <rect x="88" y="1168" width="140" height="8" rx="4" fill="${palette.accent}" />
      <text x="88" y="1234" fill="${palette.muted}" font-size="26" font-family="Avenir Next, Segoe UI, Arial, sans-serif">${escapeXml(buildCreateFooterLabel(output.platform))}</text>
    </svg>
  `.trim();
}

async function renderSvgToPngBlob(svg: string, width: number, height: number): Promise<Blob> {
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await loadImage(svgUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas rendering context unavailable.");
    }
    context.drawImage(image, 0, 0, width, height);
    const pngBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!pngBlob) {
      throw new Error("PNG export failed.");
    }
    return pngBlob;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not render slide artwork."));
    image.src = src;
  });
}
